<template>
  <FormularioCadastro
    module-name="Empresas Cliente"
    :is-loading="isLoading || isLoadingPermissoes"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :ocultar-salvar="!isEmpresaSaved"
    :desabilitar-salvar-alteracoes="isFramesSecundariosBloqueados"
    @submit="salvarEmpresa"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
  >
    <!-- GUARDIÃO: Botão de Auditoria injetado no cabeçalho -->
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.dadosBasicos = !accordionState.dadosBasicos"
      >
        <h5 class="mb-0"><i class="bi bi-info-circle me-2 text-primary"></i> Dados Básicos</h5>
        <i :class="['bi', accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.dadosBasicos">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="nomeEmpresa"
              label="Nome da Empresa"
              v-model="item.nomeEmpresa"
              @keydown.enter.prevent
              required
            />
          </div>

          <div class="col-12" v-if="!isEditing">
            <div class="form-check form-switch mt-2">
              <input class="form-check-input cursor-pointer" type="checkbox" id="incluirCategoriasComerciais" v-model="item.incluirCategoriasComerciais">
              <label class="form-check-label cursor-pointer" for="incluirCategoriasComerciais">
                <span class="fw-medium">Injetar Categorias Comerciais de Vendas/Despesas como sugestão para o Setup do Financeiro?</span><br>
                <small class="text-body-secondary">As categorias de impostos de sistema serão injetadas obrigatoriamente para garantir o funcionamento do Motor Fiscal.</small>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar e Continuar (apenas criação) -->
    <div class="mb-4" v-if="!isEmpresaSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarEmpresaLocalmente"
        :disabled="isSavingEmpresaLocal || !item.nomeEmpresa"
      >
        <span v-if="isSavingEmpresaLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Empresa e Liberar Permissões
      </button>
    </div>

    <!-- Frame 2: Permissões do Cliente (Módulos Contratados) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.permissoes = !accordionState.permissoes"
      >
        <h5 class="mb-0"><i class="bi bi-shield-check me-2 text-primary"></i> Permissões do Cliente (Módulos Contratados)</h5>
        <div class="d-flex align-items-center">
          <i :class="['bi', accordionState.permissoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
      </div>

      <div class="card-body" v-show="accordionState.permissoes">
        <div v-if="isLoadingPermissoes" class="text-center text-muted small py-4">
          <span class="spinner-border spinner-border-sm me-2"></span> A carregar permissões...
        </div>
        <div v-else-if="erroPermissoes" class="alert alert-warning border-0 shadow-sm">{{ erroPermissoes }}</div>
        <PermissoesAcessoSelector
          v-else
          :permissoesConsolidadas="permissoesConsolidadas"
          v-model:modelValue="permissoesSelecionadasIds"
          :disabled="!isEditing || !item.idEmpresa"
          :exibirControleExpiracao="true"
          :exibirMapaPermissoes="true"
        >
          <template #header-acoes>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click.stop="confirmarLiberarTodasPermissoes"
              :disabled="!isEditing || !item.idEmpresa"
            >
              <i class="bi bi-unlock me-1"></i> Liberar Todas as Permissões
            </button>
          </template>
        </PermissoesAcessoSelector>
      </div>
    </div>
    </div>

    <!-- Frame 3: Plano Contratado -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.plano = !accordionState.plano"
      >
        <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Plano Contratado</h5>
        <i :class="['bi', accordionState.plano ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.plano">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoBusca
              id="idPlanoVigente"
              label="Plano Vigente"
              v-model="item.idPlanoVigente"
              :configuracao-busca="configBuscaPlano"
              required
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="validadeContrato"
              label="Validade do Contrato"
              type="date"
              v-model="item.validadeContrato"
              required
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="quantidadeUsuariosSimultaneos"
              label="Usuários Simultâneos"
              type="number"
              min="0"
              v-model.number="item.quantidadeUsuariosimultaneos"
              required
            />
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 4: Autoridades Tributárias Padrão -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.autoridades = !accordionState.autoridades"
      >
        <h5 class="mb-0"><i class="bi bi-bank me-2 text-primary"></i> Autoridades Tributárias Padrão</h5>
        <i :class="['bi', accordionState.autoridades ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.autoridades">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBusca
              id="idAutoridadeTributariaFederal"
              label="Receita Federal"
              v-model="item.idAutoridadeTributariaFederal"
              :configuracao-busca="confBuscaAutoridadeFederal"
              :disabled="!item.idEmpresa"
              title="Necessário guardar a empresa primeiro"
            />
          </div>

          <div class="col-md-4">
            <CampoBusca
              id="idAutoridadeTributariaIbs"
              label="Autoridade Tributária IBS (Comitê Gestor)"
              v-model="item.idAutoridadeTributariaIbs"
              :configuracao-busca="confBuscaAutoridadeIbs"
              :disabled="!item.idEmpresa"
              title="Necessário guardar a empresa primeiro"
            />
          </div>

        </div>
        <small v-if="!item.idEmpresa" class="text-info mt-2 d-block">
          <i class="bi bi-info-circle me-1"></i> As autoridades só poderão ser selecionadas após o primeiro registo da empresa.
        </small>
      </div>
    </div>
    </div>

    <!-- Frame 5: Financeiro -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.financeiro = !accordionState.financeiro"
      >
        <h5 class="mb-0"><i class="bi bi-currency-dollar me-2 text-primary"></i> Financeiro</h5>
        <i :class="['bi', accordionState.financeiro ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.financeiro">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBuscaLista
              id="lstMoedaPadrao"
              label="Moeda Padrão"
              v-model="item.lstMoedaPadrao"
              tabela="Empresa"
              required
            />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista
              id="lstMoedaGerencial"
              label="Moeda Gerencial"
              v-model="item.lstMoedaGerencial"
              tabela="Empresa"
            />
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 5.5: Configurações Comerciais -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.configuracoesComerciais = !accordionState.configuracoesComerciais"
      >
        <h5 class="mb-0"><i class="bi bi-percent me-2 text-primary"></i> Configurações Comerciais</h5>
        <i :class="['bi', accordionState.configuracoesComerciais ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.configuracoesComerciais">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoInput
              id="margemEsperadaPercentual"
              label="Margem Esperada (%)"
              type="decimal"
              :casas-decimais="2"
              min="0"
              max="100"
              v-model.number="item.margemEsperadaPercentual"
              label-info="Percentual de margem esperada padrão para a empresa. Fallback máximo quando TipoProduto e Produto não especificarem."
            />
          </div>

          <div class="col-md-4">
            <CampoSelect
              id="isAprovacaoAutomatica"
              label="Aprovação Automática de Pedidos de Venda Ativa"
              v-model="parametrosComerciais.isAprovacaoAutomatica"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              label-info="Caso esta flag esteja com Sim, o pedido de venda considerará se a margem total dos itens do pedido estão >= à margem esperada deles, oriunda do modo de precificação. Esta flag não se aplicará para modos de precificação do tipo Projeto."
            />
          </div>

          <!-- Sub-frame: Numeração Automática de Documentos -->
          <div class="mt-4 border-top border-secondary-subtle pt-3">
            <h6 class="text-muted text-uppercase small fw-bold mb-3">
              <i class="bi bi-sort-numeric-up me-2"></i>Numeração Automática de Documentos
            </h6>
            <p class="small text-muted mb-3">
              Configure os templates de geração automática de código para cada tipo de documento.
              As opções disponíveis são: Prefixo, Separador, Ano (4 ou 2 dígitos), Mês, Dia, ID Filial e Sequencial.
            </p>

            <!-- Linha: Pedido de Venda -->
            <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
              <div class="d-flex align-items-center gap-3 flex-grow-1">
                <i class="bi bi-cart-check fs-5 text-primary"></i>
                <div>
                  <span class="fw-medium">Pedido de Venda</span>
                  <span class="d-block text-muted small">Código gerado na criação do Pedido de Venda</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-3">
                <span v-if="previewTemplatePedidoVenda" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplatePedidoVenda }}</span>
                <span v-else class="text-muted small">Não configurado</span>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('pedidoVenda')">
                  <i class="bi bi-pencil"></i> {{ templatePedidoVenda.length > 0 ? 'Editar' : 'Configurar' }}
                </button>
              </div>
            </div>

            <!-- Linha: Proforma Invoice -->
            <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
              <div class="d-flex align-items-center gap-3 flex-grow-1">
                <i class="bi bi-file-earmark-text fs-5 text-primary"></i>
                <div>
                  <span class="fw-medium">Proforma Invoice</span>
                  <span class="d-block text-muted small">Gerado quando o Pedido de Venda for de operação de exportação</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-3">
                <span v-if="previewTemplateProformaInvoice" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplateProformaInvoice }}</span>
                <span v-else class="text-muted small">Não configurado</span>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('proformaInvoice')">
                  <i class="bi bi-pencil"></i> {{ templateProformaInvoice.length > 0 ? 'Editar' : 'Configurar' }}
                </button>
              </div>
            </div>

            <!-- Linha: Commercial Invoice -->
            <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
              <div class="d-flex align-items-center gap-3 flex-grow-1">
                <i class="bi bi-receipt fs-5 text-primary"></i>
                <div>
                  <span class="fw-medium">Commercial Invoice</span>
                  <span class="d-block text-muted small">Gerado na emissão do Documento de Saída para exportação</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-3">
                <span v-if="previewTemplateCommercialInvoice" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplateCommercialInvoice }}</span>
                <span v-else class="text-muted small">Não configurado</span>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('commercialInvoice')">
                  <i class="bi bi-pencil"></i> {{ templateCommercialInvoice.length > 0 ? 'Editar' : 'Configurar' }}
                </button>
              </div>
            </div>

            <!-- Linha: Contrato de Venda -->
            <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
              <div class="d-flex align-items-center gap-3 flex-grow-1">
                <i class="bi bi-file-contract fs-5 text-primary"></i>
                <div>
                  <span class="fw-medium">Contrato de Venda</span>
                  <span class="d-block text-muted small">Código gerado na criação do Contrato de Venda</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-3">
                <span v-if="previewTemplateContratoVenda" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplateContratoVenda }}</span>
                <span v-else class="text-muted small">Não configurado</span>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('contratoVenda')">
                  <i class="bi bi-pencil"></i> {{ templateContratoVenda.length > 0 ? 'Editar' : 'Configurar' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Sub-frame: Listas para Atributos de Clientes -->
          <div class="mt-4 border-top border-secondary-subtle pt-3">
            <h6 class="text-muted text-uppercase small fw-bold mb-3">
              <i class="bi bi-list-check me-2"></i>Listas para Atributos de Clientes
            </h6>
            <p class="small text-muted mb-3">
              Configure quais listas personalizadas serão exigidas no cadastro de parceiros com função Cliente.
            </p>
            <div class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>Prefixo</th>
                    <th>Nome da Lista</th>
                    <th class="text-end" style="width: 80px;">
                      <button type="button" class="btn btn-sm btn-primary py-0" @click="abrirModalBuscaListaCliente">
                        <i class="bi bi-plus-lg"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(prefixo, idx) in item.listasVinculadasClientes" :key="idx">
                    <td><code class="badge bg-body-secondary text-body border">{{ prefixo }}</code></td>
                    <td>{{ obterNomeListaCliente(prefixo) }}</td>
                    <td class="text-end">
                      <button type="button" class="btn-icon text-danger" @click="removerListaCliente(idx)">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!item.listasVinculadasClientes || item.listasVinculadasClientes.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted">Nenhuma lista configurada para atributos de clientes.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 5.6: Parâmetros de Comissão por Papel Comercial -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.parametrosComissaoPapel = !accordionState.parametrosComissaoPapel"
      >
        <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-primary"></i> Parâmetros de Comissão por Papel Comercial</h5>
        <i :class="['bi', accordionState.parametrosComissaoPapel ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.parametrosComissaoPapel">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <p class="small text-muted mb-0">
            Configure o percentual de comissão e a geração de comissão em cascata para cada papel comercial.
            Os papéis são sincronizados automaticamente com a lista estrutural PARCEIRO_VND_PAPEL_COM.
          </p>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="sincronizarPapeisComissao"
            :disabled="!item.idEmpresa || isLoadingParametrosComissao"
          >
            <i class="bi bi-arrow-repeat me-1"></i>
            <span v-if="isLoadingParametrosComissao" class="spinner-border spinner-border-sm me-1"></span>
            Verificar Novos Papéis
          </button>
        </div>

        <div v-if="isLoadingParametrosComissao && parametrosComissao.length === 0" class="text-center text-muted small py-4">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Carregando parâmetros...
        </div>

        <div v-else-if="parametrosComissao.length === 0 && item.idEmpresa" class="text-center py-4 text-muted">
          <i class="bi bi-info-circle me-2"></i>
          Nenhum parâmetro encontrado. Clique em "Verificar Novos Papéis" para sincronizar.
        </div>

        <div v-else-if="parametrosComissao.length === 0 && !item.idEmpresa" class="text-center py-4 text-muted">
          <i class="bi bi-info-circle me-2"></i>
          Salve a empresa primeiro para configurar os parâmetros de comissão.
        </div>

        <div class="table-responsive" v-else>
          <table class="table table-sm table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;">#</th>
                <th>Papel Comercial</th>
                <th style="width: 160px;">% Comissão</th>
                <th style="width: 180px;">Gera Comissão Cascata</th>
                <th style="width: 120px;">Ativo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(param, idx) in parametrosComissao" :key="param.idEmpresaParametroComissaoPapel">
                <td class="text-muted fw-medium">{{ idx + 1 }}</td>
                <td>
                  <span class="fw-medium">{{ param.nomePapel || param.lstPapelComercial }}</span>
                  <br><small class="text-muted">{{ param.lstPapelComercial }}</small>
                </td>
                <td style="max-width: 160px;">
                  <CampoInput
                    :id="'percentualComissao_' + param.idEmpresaParametroComissaoPapel"
                    type="percentual"
                    v-model.number="param.percentualComissao"
                    @blur="salvarParametroComissao(param)"
                  />
                </td>
                <td style="max-width: 180px;">
                  <CampoSelect
                    :id="'geraCascata_' + param.idEmpresaParametroComissaoPapel"
                    v-model="param.geraComissaoCascata"
                    :options="simNaoOptions"
                    option-value="id"
                    option-label="label"
                    @update:model-value="salvarParametroComissao(param)"
                  />
                </td>
                <td style="max-width: 120px;">
                  <CampoSelect
                    :id="'isAtivo_' + param.idEmpresaParametroComissaoPapel"
                    v-model="param.isAtivo"
                    :options="simNaoOptions"
                    option-value="id"
                    option-label="label"
                    @update:model-value="salvarParametroComissao(param)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 6: Templates de Precificação (Comex) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="isEditing">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.templates = !accordionState.templates"
      >
        <h5 class="mb-0"><i class="bi bi-calculator me-2 text-primary"></i> Templates de Precificação (Comex)</h5>
        <i :class="['bi', accordionState.templates ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.templates">
        <div class="d-flex justify-content-end mb-3">
          <button type="button" class="btn btn-sm btn-primary" @click="novoTemplate">
            <i class="bi bi-plus-lg me-1"></i> Novo Template
          </button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th>Nome do Template</th>
                <th>Atributos</th>
                <th class="text-center">Status</th>
                <th class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="temp in templates" :key="temp.idTemplatePrecificacao">
                <td>{{ temp.nome }}</td>
                <td>
                  <span v-for="attr in parseJson(temp.estruturaJsonSchema)" :key="attr.chave" class="badge bg-secondary-subtle text-secondary-emphasis me-1">
                    {{ attr.label }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="['badge', (listas.LstStatus || []).find(s => String(s.idListaSistemaItem) === String(temp.lstStatus) || String(s.idListaClienteItem) === String(temp.lstStatus))?.textoLista?.toUpperCase().includes('ATIVO') ? 'badge-ghost-success' : 'badge-ghost-secondary']">
                    {{ getTextoLista(listas.LstStatus, temp.lstStatus) }}
                  </span>
                </td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm btn-outline-primary me-1" @click="editarTemplate(temp)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="excluirTemplate(temp.idTemplatePrecificacao)">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="templates.length === 0">
                <td colspan="3" class="text-center text-muted py-3">Nenhum template cadastrado para esta empresa.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 7: Configurações de IA -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="isEditing">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.configuracoesIA = !accordionState.configuracoesIA"
      >
        <h5 class="mb-0"><i class="bi bi-robot me-2 text-primary"></i> Configurações de IA</h5>
        <i :class="['bi', accordionState.configuracoesIA ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.configuracoesIA">
        <!-- API Key Principal da Empresa (compartilhada) -->
        <div class="row g-3 mb-3 border-bottom pb-3">
          <div class="col-md-8">
            <CampoInput
              id="apiKeyIa"
              label="API Key Principal (compartilhada)"
              type="password"
              v-model="item.apiKeyIa"
              placeholder="sk-..."
            />
            <small class="text-muted">
              <i class="bi bi-info-circle me-1"></i>
              Chave padrão para todas as integrações. Se uma integração específica tiver sua própria chave no cadastro, ela terá prioridade sobre esta.
            </small>
          </div>
          <div class="col-md-4 d-flex align-items-end pb-3">
            <code class="small text-muted" v-if="item.apiKeyIaMasked">
              <i class="bi bi-key me-1"></i>Atual: {{ item.apiKeyIaMasked }}
            </code>
          </div>
        </div>

        <div class="d-flex justify-content-end mb-3">
          <button type="button" class="btn btn-sm btn-primary" @click="novaIntegracaoAi">
            <i class="bi bi-plus-lg me-1"></i> Nova Integração
          </button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 30px;"></th>
                <th>Provedor</th>
                <th>API Key</th>
                <th>Aplicações</th>
                <th class="text-center">Status</th>
                <th class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="integ in integracoesAi" :key="integ.idClienteIntegracaoAi">
                <tr>
                  <td class="text-center">
                    <button
                      v-if="integ.aplicacoes && integ.aplicacoes.length > 0"
                      type="button"
                      class="btn btn-sm p-0 border-0 bg-transparent"
                      @click="toggleExpandIntegracao(integ.idClienteIntegracaoAi)"
                    >
                      <i :class="['bi', expandedIaRows[integ.idClienteIntegracaoAi] ? 'bi-chevron-down' : 'bi-chevron-right', 'text-secondary']"></i>
                    </button>
                  </td>
                  <td>
                    <span class="fw-medium">{{ getTextoLista(listasIa.LstProvider || [], integ.lstProvider) }}</span>
                  </td>
                  <td>
                    <code class="small">{{ integ.apiKeyMasked || item.apiKeyIaMasked || '-' }}</code>
                    <span v-if="!integ.apiKeyMasked && item.apiKeyIaMasked" class="badge bg-info-subtle text-info-emphasis ms-1" style="font-size: 0.65rem;">compartilhada</span>
                    <span v-if="integ.apiKeyMasked" class="badge bg-warning-subtle text-warning-emphasis ms-1" style="font-size: 0.65rem;">exclusiva</span>
                  </td>
                  <td>
                    <span v-if="integ.aplicacoes && integ.aplicacoes.length > 0" class="text-muted small">
                      {{ integ.aplicacoes.length }} aplicação(ões)
                    </span>
                    <span v-else class="text-muted small">-</span>
                  </td>
                  <td class="text-center">
                    <span :class="['badge', integ.lstStatus === ST.Ativo ? 'badge-ghost-success' : 'badge-ghost-secondary']">
                      {{ getTextoLista(listasIa.LstStatus || [], integ.lstStatus) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-primary me-1" @click="editarIntegracaoAi(integ)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="confirmarExclusaoIntegracaoAi(integ.idClienteIntegracaoAi)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
                <!-- Sub-linha expansível com as aplicações -->
                <tr v-if="expandedIaRows[integ.idClienteIntegracaoAi] && integ.aplicacoes && integ.aplicacoes.length > 0">
                  <td colspan="6" class="p-0 bg-body-tertiary">
                    <div class="px-4 py-2 border-start border-2 border-primary ms-3 my-1">
                      <div v-for="app in integ.aplicacoes" :key="app.idClienteIntegracaoAiAplicacoes" class="d-flex align-items-center gap-2 mb-1">
                        <span class="badge badge-ghost-info">{{ getTextoLista(listasIa.LstAplicacoesAi || [], app.lstAplicacoesAi) }}</span>
                        <span class="text-muted small" v-if="app.observacoes">
                          <i class="bi bi-chat-dots me-1"></i>{{ app.observacoes }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="integracoesAi.length === 0">
                <td colspan="6" class="text-center text-muted py-3">
                  Nenhuma integração de IA cadastrada para esta empresa.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 8: Templates de Relatório -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="isEditing">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.templatesRelatorio = !accordionState.templatesRelatorio"
      >
        <h5 class="mb-0"><i class="bi bi-file-earmark-pdf me-2 text-primary"></i> Templates de Relatório</h5>
        <i :class="['bi', accordionState.templatesRelatorio ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.templatesRelatorio">
        <div v-if="isLoadingTemplatesRelatorio" class="text-center text-muted small py-4">
          <span class="spinner-border spinner-border-sm me-2"></span> A carregar templates...
        </div>

        <div v-else-if="templatesRelatorio.length === 0" class="text-center text-muted py-4">
          <i class="bi bi-file-earmark-pdf fs-4 d-block mb-2 text-secondary"></i>
          <p class="mb-0">Nenhum template de relatório disponível.</p>
          <small>Cadastre templates no módulo de administração do sistema.</small>
        </div>

        <div v-else>
          <p class="small text-muted mb-3">
            <span class="fw-medium text-body">{{ totalTemplatesHabilitados }} de {{ templatesRelatorio.length }} templates habilitados.</span>
            Clique em uma aplicação para gerenciar seus templates e imagens.
          </p>
          <div class="d-flex flex-wrap gap-2">
            <button
              v-for="app in aplicacoesOrdenadas"
              :key="app.codigoModulo"
              type="button"
              class="badge rounded-pill d-inline-flex align-items-center gap-2 px-3 py-2 cursor-pointer"
              :class="app.habilitados > 0 ? 'badge-ghost-success' : 'badge-ghost-warning'"
              :title="app.habilitados + ' de ' + app.total + ' templates ativados'"
              @click="abrirModalTemplatesRelatorio(app.codigoModulo)"
            >
              {{ app.codigoModulo }}
              <span class="badge rounded-pill bg-body-secondary text-body">{{ app.habilitados }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 9.5: Configurações de Sessão / Token JWT -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.tokenJwt = !accordionState.tokenJwt"
      >
        <h5 class="mb-0"><i class="bi bi-shield-lock me-2 text-primary"></i> Configurações de Sessão / Token JWT</h5>
        <i :class="['bi', accordionState.tokenJwt ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.tokenJwt">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoInput
              id="tokenExpiracaoMinutos"
              label="Expiração do Access Token (minutos)"
              type="number"
              min="1"
              v-model.number="item.tokenExpiracaoMinutos"
              descricao-informativa="Deixe vazio para usar o fallback do sistema (15 min)."
            />
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 8: Configurações de Compras (unificado) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="isEditing">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.configuracoesCompras = !accordionState.configuracoesCompras"
      >
        <h5 class="mb-0"><i class="bi bi-cart me-2 text-primary"></i> Configurações de Compras</h5>
        <i :class="['bi', accordionState.configuracoesCompras ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.configuracoesCompras">
        <!-- Sub-seção 1: Tipos de Documento de Entrada -->
        <div class="mb-4">
          <h6 class="text-muted text-uppercase small fw-bold mb-3">
            <i class="bi bi-file-earmark-text me-2"></i>Tipos de Documento de Entrada
          </h6>
          <div class="d-flex justify-content-end mb-3">
            <button type="button" class="btn btn-sm btn-primary" @click="novoDocumentoEntradaTipo">
              <i class="bi bi-plus-lg me-1"></i> Novo Tipo de Documento
            </button>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th class="text-center">Documento Interno</th>
                  <th>Template</th>
                  <th class="text-center">Status</th>
                  <th class="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in documentosEntradaTipos" :key="doc.idTipoDocumentoEntrada">
                  <td>{{ doc.nome }}</td>
                  <td>
                    <span class="text-truncate d-inline-block" style="max-width: 250px;">{{ doc.descricao }}</span>
                  </td>
                  <td class="text-center">
                    <span :class="['badge', doc.isDocumentoInterno ? 'badge-ghost-info' : 'badge-ghost-secondary']">
                      {{ doc.isDocumentoInterno ? 'Sim' : 'Não' }}
                    </span>
                  </td>
                  <td>
                    <code>{{ gerarPreviewTemplate(parseTemplateNumeracao(doc.configTemplateNumero)) || '-' }}</code>
                  </td>
                  <td class="text-center">
                    <span :class="['badge', (listas.LstStatus || []).find(s => String(s.idListaSistemaItem) === String(doc.lstStatus) || String(s.idListaClienteItem) === String(doc.lstStatus))?.textoLista?.toUpperCase().includes('ATIVO') ? 'badge-ghost-success' : 'badge-ghost-secondary']">
                      {{ getTextoLista(listas.LstStatus, doc.lstStatus) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-primary me-1" @click="editarDocumentoEntradaTipo(doc)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="excluirDocumentoEntradaTipo(doc.idTipoDocumentoEntrada)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="documentosEntradaTipos.length === 0">
                  <td colspan="6" class="text-center text-muted py-3">
                    Nenhum tipo de documento de entrada cadastrado para esta empresa.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Sub-seção 2: Numeração Automática de Documentos -->
        <div class="mt-4 border-top border-secondary-subtle pt-3">
          <h6 class="text-muted text-uppercase small fw-bold mb-3">
            <i class="bi bi-sort-numeric-up me-2"></i>Numeração Automática de Documentos
          </h6>
          <p class="small text-muted mb-3">
            Configure os templates de geração automática de código para documentos de compras.
            As opções disponíveis são: Prefixo, Separador, Ano (4 ou 2 dígitos), Mês, Dia, ID Filial e Sequencial.
          </p>

          <!-- Linha: Ordem de Compra Nacional -->
          <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
            <div class="d-flex align-items-center gap-3 flex-grow-1">
              <i class="bi bi-cart-check fs-5 text-primary"></i>
              <div>
                <span class="fw-medium">Ordem de Compra Nacional</span>
                <span class="d-block text-muted small">Código gerado na criação da Ordem de Compra Nacional</span>
              </div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <span v-if="previewTemplateOrdemCompra" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplateOrdemCompra }}</span>
              <span v-else class="text-muted small">Não configurado</span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('ordemCompra')">
                <i class="bi bi-pencil"></i> {{ templateOrdemCompra.length > 0 ? 'Editar' : 'Configurar' }}
              </button>
            </div>
          </div>

          <!-- Linha: Contrato de Compra -->
          <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
            <div class="d-flex align-items-center gap-3 flex-grow-1">
              <i class="bi bi-file-contract fs-5 text-primary"></i>
              <div>
                <span class="fw-medium">Contrato de Compra</span>
                <span class="d-block text-muted small">Código gerado na criação do Contrato de Compra</span>
              </div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <span v-if="previewTemplateContratoCompra" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplateContratoCompra }}</span>
              <span v-else class="text-muted small">Não configurado</span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('contratoCompra')">
                <i class="bi bi-pencil"></i> {{ templateContratoCompra.length > 0 ? 'Editar' : 'Configurar' }}
              </button>
            </div>
          </div>

          <!-- Linha: Purchase Order -->
          <div class="d-flex align-items-center justify-content-between p-3 mb-2 rounded" style="background-color: rgba(13, 110, 253, 0.05);">
            <div class="d-flex align-items-center gap-3 flex-grow-1">
              <i class="bi bi-cart-check fs-5 text-primary"></i>
              <div>
                <span class="fw-medium">Purchase Order</span>
                <span class="d-block text-muted small">Código gerado na criação da Purchase Order</span>
              </div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <span v-if="previewTemplatePurchaseOrder" class="badge badge-ghost-info fs-6 px-3 py-2">{{ previewTemplatePurchaseOrder }}</span>
              <span v-else class="text-muted small">Não configurado</span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalTemplateNumeracao('purchaseOrder')">
                <i class="bi bi-pencil"></i> {{ templatePurchaseOrder.length > 0 ? 'Editar' : 'Configurar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 9: Repositório de Imagens (extraído para componente) -->
    <RepositorioImagensEmpresa
      v-if="isEditing"
      :id-empresa="item.idEmpresa"
      @show-notification="(p) => emit('show-notification', p)"
    />

  </FormularioCadastro>

  <!-- Modal de Busca de Lista para Atributos de Clientes -->
  <ModalDeBusca
    v-if="mostrarModalBuscaListaCliente"
    :configuracao="configBuscaListaCliente"
    @item-selecionado="onListaClienteSelecionada"
    @fechar="mostrarModalBuscaListaCliente = false"
  />

  <!-- Modal de Diagnóstico -->
  <ModalFormulario
    v-model="mostrarRelatorio"
    titulo="Diagnóstico de Conformidade Fiscal"
    :ocultar-confirmar="true"
    tamanho="xl"
    @update:model-value="aoFecharModal"
  >
    <DiagnosticoFiscalRelatorio
      :modo-multi-empresa="dadosRelatorio.modoMultiEmpresa"
      :nivel-antigo="dadosRelatorio.nivelAntigo"
      :nivel-novo="dadosRelatorio.nivelNovo"
      :empresas-com-pendencias="dadosRelatorio.empresasComPendencias"
      :pendencias="dadosRelatorio.pendencias"
      :pode-fazer-upgrade="dadosRelatorio.podeFazerUpgrade"
      :total-pendencias="dadosRelatorio.totalPendencias"
      :is-loading="dadosRelatorio.isLoading"
      @fechar="mostrarRelatorio = false"
    />
  </ModalFormulario>

  <!-- Modal para Cadastro/Edição de Template -->
  <ModalFormulario
    v-model="modalTemplate.visivel"
    :titulo="modalTemplate.item.idTemplatePrecificacao ? 'Editar Template' : 'Novo Template'"
    @confirmar="salvarTemplate"
  >
    <div class="row g-3">
      <div class="col-md-8">
        <CampoInput
          id="templateNome"
          label="Nome do Template"
          v-model="modalTemplate.item.nome"
          required
        />
      </div>
      <div class="col-md-4">
        <CampoBuscaLista
          id="lstStatus"
          label="Status"
          v-model="modalTemplate.item.lstStatus"
          tabela="TemplatePrecificacao"
          required
        />
      </div>

      <div class="col-12 mt-4">
        <h6 class="border-bottom pb-2 mb-3">Definição dos Atributos Dinâmicos</h6>
        <div class="table-responsive border rounded p-2 bg-body-tertiary">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Chave (ID no JSON)</th>
                <th>Label (Exibição)</th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(attr, index) in modalTemplate.atributos" :key="index">
                <td>
                  <input type="text" class="form-control form-control-sm" v-model="attr.chave" placeholder="Ex: lme" required>
                </td>
                <td>
                  <input type="text" class="form-control form-control-sm" v-model="attr.label" placeholder="Ex: Valor LME" required>
                </td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm text-danger" @click="removerAtributo(index)">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
              <tr>
                <td colspan="3">
                  <button type="button" class="btn btn-sm btn-link text-primary p-0" @click="adicionarAtributo">
                    <i class="bi bi-plus-lg me-1"></i> Adicionar Atributo
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <small class="text-muted mt-2 d-block">
          Estes campos serão renderizados automaticamente como campos numéricos (moeda) na tela de Contrato/Pedido.
        </small>
      </div>
    </div>
  </ModalFormulario>

  <!-- Modal para Cadastro/Edição de Integração de IA -->
  <ModalFormulario
    v-model="modalIntegracaoAi.visivel"
    :titulo="modalIntegracaoAi.item.idClienteIntegracaoAi ? 'Editar Integração de IA' : 'Nova Integração de IA'"
    :loading="modalIntegracaoAi.salvando"
    @confirmar="salvarIntegracaoAi"
  >
    <div class="row g-3">
    <!-- Bloco: Identificação -->
    <div class="col-12">
      <h6 class="border-bottom pb-2 mb-3 text-primary">
        <i class="bi bi-tag me-1"></i> Identificação
      </h6>
    </div>
    <div class="col-md-6">
      <CampoSelect
        id="lstProviderIntegracao"
        label="Provedor"
        v-model="modalIntegracaoAi.item.lstProvider"
        :options="listasIa.LstProvider || []"
        option-label="textoLista"
        option-value="idListaSistemaItem"
        required
      />
    </div>

    <div class="col-md-3">
      <CampoSelect
        id="lstStatusIntegracao"
        label="Status"
        v-model="modalIntegracaoAi.item.lstStatus"
        :options="listasIa.LstStatus || []"
        option-label="textoLista"
        option-value="idListaSistemaItem"
        required
      />
    </div>

    <div class="col-12">
      <CampoInput
        id="apiKeyIntegracao"
        label="API Key (opcional)"
        type="password"
        v-model="modalIntegracaoAi.item.apiKey"
        :placeholder="item.apiKeyIaMasked ? 'Deixe vazio para usar a chave principal da empresa' : 'sk-...'"
      />
      <small class="text-muted">
        <i class="bi bi-info-circle me-1"></i>
        Preencha apenas se esta integração precisar de uma chave diferente da chave principal da empresa.
        A chave será criptografada e armazenada com segurança.
      </small>
    </div>

    <!-- Bloco: Configuração do Modelo -->
    <div class="col-12 mt-3">
      <h6 class="border-bottom pb-2 mb-3 text-primary">
        <i class="bi bi-cpu me-1"></i> Configuração do Modelo
      </h6>
    </div>
    <div class="col-md-8">
      <CampoInput
        id="urlEndpoint"
        label="URL Endpoint"
        v-model="modalIntegracaoAi.item.urlEndpoint"
        placeholder="https://api.openai.com/v1"
      />
    </div>
    <div class="col-md-4">
      <CampoInput
        id="model"
        label="Modelo"
        type="textarea"
        v-model="modalIntegracaoAi.item.model"
        placeholder="Ex: google/gemini-2.5-flash-lite"
      />
      <small class="text-muted">
        <i class="bi bi-info-circle me-1"></i>
        Insira um modelo por linha para fallback automático em caso de indisponibilidade do principal.
      </small>
    </div>

    <div class="col-md-3">
      <CampoInput
        id="temperature"
        label="Temperature"
        type="number"
        step="0.1"
        min="0"
        max="2"
        v-model.number="modalIntegracaoAi.item.temperature"
      />
    </div>
    <div class="col-md-3">
      <CampoInput
        id="maxTokens"
        label="Max Tokens"
        type="number"
        min="1"
        v-model.number="modalIntegracaoAi.item.maxTokens"
      />
    </div>
    <div class="col-md-3">
      <CampoInput
        id="timeoutSeconds"
        label="Timeout (segundos)"
        type="number"
        min="1"
        v-model.number="modalIntegracaoAi.item.timeoutSeconds"
      />
    </div>
    <div class="col-md-3">
      <CampoInput
        id="responseFormat"
        label="Response Format"
        v-model="modalIntegracaoAi.item.responseFormat"
        placeholder="text, json_object"
      />
    </div>

    <div class="col-12">
      <div class="form-check form-switch mt-2">
        <input class="form-check-input cursor-pointer" type="checkbox" id="enableStreaming" v-model="modalIntegracaoAi.item.enableStreaming">
        <label class="form-check-label cursor-pointer" for="enableStreaming">
          <span class="fw-medium">Habilitar Streaming</span>
        </label>
      </div>
    </div>

    <div class="col-12">
      <CampoInput
        id="observacoesIntegracao"
        label="Observações"
        type="textarea"
        v-model="modalIntegracaoAi.item.observacoes"
      />
    </div>

      <!-- Aplicações Vinculadas -->
      <div class="col-12 mt-3">
        <h6 class="border-bottom pb-2 mb-3">
          <i class="bi bi-app me-1"></i> Aplicações Vinculadas
        </h6>
        <div class="table-responsive border rounded p-2 bg-body-tertiary">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Aplicação</th>
                <th>Observações</th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(app, index) in modalIntegracaoAi.aplicacoes" :key="index">
                <td>
                  <span class="badge badge-ghost-info">
                    {{ getTextoLista(listasIa.LstAplicacoesAi || [], app.lstAplicacoesAi) }}
                  </span>
                </td>
                <td class="text-muted small">{{ app.observacoes || '-' }}</td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm text-danger" @click="removerAplicacao(index)">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="modalIntegracaoAi.aplicacoes.length === 0">
                <td colspan="3" class="text-center text-muted py-2">
                  Nenhuma aplicação vinculada.
                </td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="btn btn-sm btn-link text-primary p-0 mt-2" @click="abrirModalAplicacao">
            <i class="bi bi-plus-lg me-1"></i> Adicionar Aplicação
          </button>
        </div>
      </div>

      <div class="col-12" v-if="modalIntegracaoAi.item.apiKey || item.apiKeyIa">
        <button
          type="button"
          class="btn btn-outline-info btn-sm"
          @click="testarConexaoIntegracaoAi"
          :disabled="modalIntegracaoAi.testando"
        >
          <span v-if="modalIntegracaoAi.testando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-plug me-1"></i>
          Testar Conexão
        </button>
        <small v-if="modalIntegracaoAi.resultadoTeste" class="ms-2" :class="modalIntegracaoAi.resultadoTeste.sucesso ? 'text-success' : 'text-danger'">
          {{ modalIntegracaoAi.resultadoTeste.mensagem }}
        </small>
      </div>
    </div>
  </ModalFormulario>

  <!-- Modal para Adicionar Aplicação à Integração de IA -->
  <ModalFormulario
    v-model="modalIntegracaoAiAplicacao.visivel"
    titulo="Adicionar Aplicação"
    :loading="modalIntegracaoAiAplicacao.salvando"
    @confirmar="confirmarAdicionarAplicacao"
  >
    <div class="row g-3">
      <div class="col-12">
        <CampoBuscaLista
          id="lstAplicacoesAi"
          label="Aplicação"
          v-model="modalIntegracaoAiAplicacao.item.lstAplicacoesAi"
          tabela="ClienteIntegracaoAiAplicacoes"
          required
        />
      </div>
      <div class="col-12">
        <CampoInput
          id="obsAplicacaoModal"
          label="Observações"
          v-model="modalIntegracaoAiAplicacao.item.observacoes"
        />
      </div>
    </div>
  </ModalFormulario>

  <!-- GUARDIÃO: Componente de Auditoria que se conecta automaticamente à tabela Empresa -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Empresa"
    contexto="master"
    :chave-primaria="item.idEmpresa"
    :titulo-registro="item.nomeEmpresa"
    :tabelas-filhas="['EmpresaRegraAcesso']"
    chave-estrangeira="IdEmpresa"
    @fechar="mostrarAuditoria = false"
  />

  <!-- Confirmation Modal para exclusão de Integração de IA -->
  <ConfirmationModal
    ref="confirmacaoIaRef"
    title="Excluir Integração de IA"
    message="Deseja realmente excluir esta integração de IA?"
    confirm-button-text="Sim, Excluir"
    cancel-button-text="Cancelar"
    @confirmed="aoConfirmarExclusaoIa"
  />

  <!-- Confirmation Modal para Liberar Todas as Regras de Acesso -->
  <ConfirmationModal
    ref="confirmacaoLiberarPermissoesRef"
    title="Liberar Todas as Permissões de Acesso"
    message="Tem certeza que deseja liberar todas as permissões de acesso para esta empresa? Esta ação não pode ser desfeita facilmente."
    confirm-button-text="Sim, Liberar Todas"
    cancel-button-text="Cancelar"
    @confirmed="liberarTodasPermissoes"
  />

  <!-- Modal para Cadastro/Edição de Tipo de Documento de Entrada -->
  <ModalFormulario
    v-model="modalDocumentoEntrada.visivel"
    :titulo="modalDocumentoEntrada.item.idTipoDocumentoEntrada ? 'Editar Tipo de Documento' : 'Novo Tipo de Documento'"
    :loading="modalDocumentoEntrada.salvando"
    @confirmar="salvarDocumentoEntradaTipo"
  >
    <div class="row g-3">
      <div class="col-md-6">
        <CampoInput
          id="nomeDocumentoEntrada"
          label="Nome"
          v-model="modalDocumentoEntrada.item.nome"
          required
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="descricaoDocumentoEntrada"
          label="Descrição"
          v-model="modalDocumentoEntrada.item.descricao"
          required
        />
      </div>

      <div class="col-md-6">
        <CampoSelect
          id="isDocumentoInterno"
          label="Documento Interno?"
          v-model="modalDocumentoEntrada.item.isDocumentoInterno"
          :options="simNaoOptions"
          option-value="id"
          option-label="label"
          required
        />
      </div>

      <div class="col-md-6">
        <CampoSelect
          id="exigePreenchimentoSerie"
          label="Exige Preenchimento de Série?"
          v-model="modalDocumentoEntrada.item.exigePreenchimentoSerie"
          :options="simNaoOptions"
          option-value="id"
          option-label="label"
        />
      </div>

      <div class="col-md-6">
        <CampoBuscaLista
          id="lstStatus"
          label="Status"
          v-model="modalDocumentoEntrada.item.lstStatus"
          tabela="DocumentoEntradaTipo"
          required
        />
      </div>

      <div class="col-md-6">
        <CampoInput
          id="codigoModeloFiscal"
          label="Modelo Fiscal (SPED - COD_MOD)"
          v-model="modalDocumentoEntrada.item.codigoModeloFiscal"
          maxlength="2"
          placeholder="Ex: 55"
        />
      </div>

      <template v-if="modalDocumentoEntrada.item.isDocumentoInterno">
        <div class="col-12">
          <hr class="my-2">
          <h6 class="text-primary mb-3">
            <i class="bi bi-gear me-1"></i> Configuração de Geração Automática de Código
          </h6>
          <p class="text-muted small mb-3">
            <i class="bi bi-info-circle me-1"></i>
            Configure as partes que compõem o código. O <strong>Sequencial</strong> é obrigatório e deve ser sempre o último elemento.
          </p>
        </div>

        <div class="col-12">
          <div class="d-flex justify-content-end mb-2">
            <button type="button" class="btn btn-sm btn-primary" @click="adicionarParteDocEntrada">
              <i class="bi bi-plus-lg me-1"></i> Adicionar Parte
            </button>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th style="width: 40px;">#</th>
                  <th>Tipo</th>
                  <th>Valor / Configuração</th>
                  <th style="width: 90px;" class="text-center">Ordem</th>
                  <th style="width: 80px;" class="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(parte, index) in docEntradaPartes" :key="index">
                  <td class="text-muted small">{{ index + 1 }}</td>
                  <td>
                    <select class="form-select form-select-sm" v-model="parte.tipo" @change="aoAlterarTipoParteDocEntrada(index)">
                      <option value="prefixo">Prefixo</option>
                      <option value="separador">Separador</option>
                      <option value="ano4">Ano (4 dígitos)</option>
                      <option value="ano2">Ano (2 dígitos)</option>
                      <option value="mes">Mês</option>
                      <option value="dia">Dia</option>
                      <option value="idFilial">ID Filial</option>
                      <option value="sequencial">Sequencial</option>
                    </select>
                  </td>
                  <td>
                    <template v-if="parte.tipo === 'prefixo'">
                      <input type="text" class="form-control form-control-sm" v-model="parte.valor" maxlength="5" placeholder="Ex: NFS">
                    </template>
                    <template v-else-if="parte.tipo === 'separador'">
                      <select class="form-select form-select-sm" v-model="parte.valor">
                        <option value="" disabled>Selecione...</option>
                        <option value="-">- (hífen)</option>
                        <option value="/">/ (barra)</option>
                        <option value=".">. (ponto)</option>
                        <option value="_">_ (underline)</option>
                      </select>
                    </template>
                    <template v-else-if="parte.tipo === 'sequencial'">
                      <div class="d-flex align-items-center gap-2">
                        <input type="number" class="form-control form-control-sm" v-model.number="parte.digitos" min="1" max="10" placeholder="Dígitos" style="width: 100px;">
                        <small class="text-muted">dígitos</small>
                      </div>
                    </template>
                    <template v-else>
                      <span class="text-muted small">Sem configuração adicional</span>
                    </template>
                  </td>
                  <td class="text-center">
                    <div class="btn-group btn-group-sm">
                      <button type="button" class="btn btn-outline-secondary" :disabled="index === 0" @click="moverParteDocEntrada(index, -1)" title="Subir">
                        <i class="bi bi-chevron-up"></i>
                      </button>
                      <button type="button" class="btn btn-outline-secondary" :disabled="index === docEntradaPartes.length - 1" @click="moverParteDocEntrada(index, 1)" title="Descer">
                        <i class="bi bi-chevron-down"></i>
                      </button>
                    </div>
                  </td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm text-danger" @click="removerParteDocEntrada(index)">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="docEntradaPartes.length === 0">
                  <td colspan="5" class="text-center text-muted py-3">
                    Nenhuma parte configurada. Adicione ao menos o <strong>Sequencial</strong> (obrigatório, sempre ao final).
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="docEntradaSequencialNaoFinal" class="alert alert-warning border-0 shadow-sm py-2 mb-3">
            <i class="bi bi-exclamation-triangle me-1"></i>
            O <strong>Sequencial</strong> deve ser sempre o último elemento do template. Use os botões de ordem para movê-lo ao final.
          </div>

          <div v-if="docEntradaPreview" class="d-flex align-items-center gap-2 p-3 rounded" style="background-color: rgba(13, 110, 253, 0.08);">
            <i class="bi bi-eye-fill text-primary"></i>
            <span class="fw-medium text-body-secondary">Preview do código gerado:</span>
            <span class="badge badge-ghost-info fs-6 px-3 py-2">{{ docEntradaPreview }}</span>
          </div>
        </div>
      </template>
    </div>
  </ModalFormulario>

  <!-- Modal para Edição de Template de Numeração (Pedido Venda / Proforma / Commercial) -->
  <ModalFormulario
    v-model="modalTemplateNumeracao.visivel"
    :titulo="modalTemplateNumeracao.titulo"
    tamanho="lg"
    texto-confirmar="Concluir"
    @confirmar="confirmarTemplateNumeracao"
  >
    <div class="row g-3">
      <div class="col-12">
        <p class="text-muted small mb-0">
          <i class="bi bi-info-circle me-1"></i>
          Configure as partes que compõem o código. O <strong>Sequencial</strong> é obrigatório e deve ser sempre o último elemento.
        </p>
      </div>

      <div class="col-12">
        <div class="d-flex justify-content-end mb-2">
          <button type="button" class="btn btn-sm btn-primary" @click="adicionarParteTemplateNumeracao">
            <i class="bi bi-plus-lg me-1"></i> Adicionar Parte
          </button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;">#</th>
                <th>Tipo</th>
                <th>Valor / Configuração</th>
                <th style="width: 90px;" class="text-center">Ordem</th>
                <th style="width: 80px;" class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(parte, index) in modalTemplateNumeracao.partes" :key="index">
                <td class="text-muted small">{{ index + 1 }}</td>
                <td>
                  <select class="form-select form-select-sm" v-model="parte.tipo" @change="aoAlterarTipoParteNumeracao(index)">
                    <option value="prefixo">Prefixo</option>
                    <option value="separador">Separador</option>
                    <option value="ano4">Ano (4 dígitos)</option>
                    <option value="ano2">Ano (2 dígitos)</option>
                    <option value="mes">Mês</option>
                    <option value="dia">Dia</option>
                    <option value="idFilial">ID Filial</option>
                    <option value="sequencial">Sequencial</option>
                  </select>
                </td>
                <td>
                  <template v-if="parte.tipo === 'prefixo'">
                    <input type="text" class="form-control form-control-sm" v-model="parte.valor" maxlength="5" placeholder="Ex: PV">
                  </template>
                  <template v-else-if="parte.tipo === 'separador'">
                    <select class="form-select form-select-sm" v-model="parte.valor">
                      <option value="" disabled>Selecione...</option>
                      <option value="-">- (hífen)</option>
                      <option value="/">/ (barra)</option>
                      <option value=".">. (ponto)</option>
                      <option value="_">_ (underline)</option>
                    </select>
                  </template>
                  <template v-else-if="parte.tipo === 'sequencial'">
                    <div class="d-flex align-items-center gap-2">
                      <input type="number" class="form-control form-control-sm" v-model.number="parte.digitos" min="1" max="10" placeholder="Dígitos" style="width: 100px;">
                      <small class="text-muted">dígitos</small>
                    </div>
                  </template>
                  <template v-else>
                    <span class="text-muted small">Sem configuração adicional</span>
                  </template>
                </td>
                <td class="text-center">
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary" :disabled="index === 0" @click="moverParteTemplateNumeracao(index, -1)" title="Subir">
                      <i class="bi bi-chevron-up"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary" :disabled="index === modalTemplateNumeracao.partes.length - 1" @click="moverParteTemplateNumeracao(index, 1)" title="Descer">
                      <i class="bi bi-chevron-down"></i>
                    </button>
                  </div>
                </td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm text-danger" @click="removerParteTemplateNumeracao(index)">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="modalTemplateNumeracao.partes.length === 0">
                <td colspan="5" class="text-center text-muted py-3">
                  Nenhuma parte configurada. Adicione ao menos o <strong>Sequencial</strong> (obrigatório, sempre ao final).
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="modalTemplateNumeracao.sequencialNaoFinal" class="alert alert-warning border-0 shadow-sm py-2 mb-3">
          <i class="bi bi-exclamation-triangle me-1"></i>
          O <strong>Sequencial</strong> deve ser sempre o último elemento do template. Use os botões de ordem para movê-lo ao final.
        </div>

        <div v-if="modalTemplateNumeracao.preview" class="d-flex align-items-center gap-2 p-3 rounded" style="background-color: rgba(13, 110, 253, 0.08);">
          <i class="bi bi-eye-fill text-primary"></i>
          <span class="fw-medium text-body-secondary">Preview do código gerado:</span>
          <span class="badge badge-ghost-info fs-6 px-3 py-2">{{ modalTemplateNumeracao.preview }}</span>
        </div>
      </div>
    </div>
  </ModalFormulario>

  <!-- Modal de Preview de Templates de Relatório -->
  <RelatorioPreviewModal
    :templates="previewTemplates"
    :visible="showPreviewModal"
    :id-empresa="item.idEmpresa"
    :modo-preview="true"
    @fechar="fecharPreviewRelatorio"
    @show-notification="(payload) => emit('show-notification', payload)"
  />

  <!-- Modal de Gerenciamento de Templates de Relatório -->
  <ModalFormulario
    v-model="mostrarModalTemplatesRelatorio"
    :titulo="tituloModalTemplatesRelatorio"
    tamanho="xxl"
  >
    <div class="d-flex gap-3" style="min-height: 60vh;">

      <!-- Painel B: Templates do módulo -->
      <div class="border-end pe-3" style="width: 380px; flex-shrink: 0;">
        <h6 class="text-uppercase small fw-bold text-muted mb-3">Templates</h6>
        <div v-if="templatesDoModulo.length === 0" class="text-muted small py-4 text-center">
          Selecione uma aplicação.
        </div>
        <div
          v-for="tmpl in templatesDoModulo"
          :key="tmpl.idRelatorioTemplate"
          class="p-2 mb-2 rounded border cursor-pointer"
          :class="{ 'border-primary bg-primary bg-opacity-10': tmpl.idRelatorioTemplate === templateSelecionadoId }"
          @click="selecionarTemplate(tmpl)"
        >
          <div class="d-flex align-items-start gap-2">
            <div class="form-check form-switch mt-1" @click.stop>
              <input
                class="form-check-input cursor-pointer"
                type="checkbox"
                :id="'tmpl_' + tmpl.idRelatorioTemplate"
                :checked="tmpl.habilitado"
                :disabled="!item.idEmpresa"
                @change="toggleTemplateRelatorio(tmpl)"
              >
            </div>
            <div class="flex-grow-1">
              <span class="fw-medium d-block">{{ tmpl.nome }}</span>
              <span v-if="tmpl.descricao" class="text-muted small">{{ tmpl.descricao }}</span>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click.stop="previewTemplateRelatorio(tmpl)"
              :title="'Preview: ' + tmpl.nome"
            >
              <i class="bi bi-eye"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Painel C: Imagens do template selecionado -->
      <div class="flex-grow-1">
        <h6 class="text-uppercase small fw-bold text-muted mb-3">Imagens</h6>
        <div v-if="!templateSelecionado" class="text-center text-muted py-5">
          <i class="bi bi-image fs-3 d-block mb-2"></i>
          Selecione um template para configurar as imagens.
        </div>
        <div v-else-if="!templateSelecionado.habilitado" class="alert alert-info border-0">
          <i class="bi bi-info-circle me-1"></i> Habilite o template para configurar as imagens.
        </div>
        <div v-else-if="!templateSelecionado.slotsImagem || templateSelecionado.slotsImagem.length === 0" class="text-muted small py-4 text-center">
          Este template não possui slots de imagem.
        </div>
        <div v-else>
          <div v-for="slot in templateSelecionado.slotsImagem" :key="slot.chave" class="mb-3">
            <label class="small text-muted mb-1 d-block">
              {{ slot.label }}
              <span v-if="slot.maxWidth" class="badge bg-secondary-subtle text-secondary-emphasis ms-1" style="font-size: 0.65rem;">
                {{ slot.maxWidth }} × {{ slot.maxHeight }} px
              </span>
              <span v-if="slot.maxWidth && slot.maxHeight" class="badge badge-ghost-info ms-1" style="font-size: 0.65rem;">
                Proporção {{ formatarProporcao(slot.maxWidth, slot.maxHeight) }}
              </span>
              <span v-if="slot.obrigatorio" class="text-danger">*</span>
            </label>
            <div class="d-flex align-items-start gap-3">
              <div class="w-50">
                <CampoBusca
                  :key="'img_' + slot.chave + '_' + (proporcaoResetKeys[slot.chave] || 0)"
                  :id="'imgSlot_' + slot.chave"
                  :model-value="obterIdImagemSlot(templateSelecionado, slot.chave)"
                  :configuracao-busca="configBuscaImagem"
                  :z-index-modal-busca="10060"
                  hide-label
                  @selecionado="(img) => validarProporcaoEAtualizarImagem(img, templateSelecionado, slot)"
                  @update:model-value="(val) => aoLimparImagemSlot(val, templateSelecionado, slot)"
                />
              </div>

              <!-- Preview da imagem selecionada -->
              <div v-if="obterImagemSlot(templateSelecionado, slot.chave)" class="d-flex gap-3 align-items-start border rounded p-2 bg-body-tertiary flex-grow-1">
                <img
                  :src="obterImagemSlot(templateSelecionado, slot.chave).urlImagem"
                  :alt="obterImagemSlot(templateSelecionado, slot.chave).nomeArquivoOriginal"
                  class="rounded border flex-shrink-0"
                  style="max-height: 100px; max-width: 240px; object-fit: contain;"
                >
                <div class="small lh-sm">
                  <div class="fw-medium text-truncate" :title="obterImagemSlot(templateSelecionado, slot.chave).nomeArquivoOriginal">
                    <i class="bi bi-file-earmark-image me-1"></i>{{ obterImagemSlot(templateSelecionado, slot.chave).nomeArquivoOriginal }}
                  </div>
                  <div v-if="obterImagemSlot(templateSelecionado, slot.chave).larguraOriginal" class="text-muted">
                    <i class="bi bi-arrows-angle-expand me-1"></i>{{ obterImagemSlot(templateSelecionado, slot.chave).larguraOriginal }} × {{ obterImagemSlot(templateSelecionado, slot.chave).alturaOriginal }} px
                  </div>
                  <div class="text-muted">
                    <i class="bi bi-file-type me-1"></i>{{ (obterImagemSlot(templateSelecionado, slot.chave).formatoArquivo || '').toUpperCase() }}
                    <span v-if="obterImagemSlot(templateSelecionado, slot.chave).tamanhoBytes" class="ms-2">
                      <i class="bi bi-database me-1"></i>{{ formatarTamanhoArquivo(obterImagemSlot(templateSelecionado, slot.chave).tamanhoBytes) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-secondary" @click="mostrarModalTemplatesRelatorio = false">
        Fechar
      </button>
    </template>
  </ModalFormulario>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario.js'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { ST } from '@/constants/ST'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import RepositorioImagensEmpresa from '@/components/empresasClientes/empresas/RepositorioImagensEmpresa.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import DiagnosticoFiscalRelatorio from '@/components/fiscal/DiagnosticoFiscalRelatorio.vue'
import RelatorioPreviewModal from '@/components/relatorios/RelatorioPreviewModal.vue'
import { configBuscaPlano } from '@/search-definitions/planoSearch.js'
import { criarConfigBuscaEmpresaImagem } from '@/search-definitions/empresaImagemSearch'
import { criarConfigBuscaParceiroAutoridadeTributaria } from '@/search-definitions/parceiroAutoridadeTributariaSearch'
import { PARCEIRO_ESFERA_GOV } from '@/constants/PARCEIRO_ESFERA_GOV'
import { dataHojeBR } from '@/utils/useDataBR'
import { formatarProporcao, proporcaoAtende } from '@/utils/imagemProporcao'
import PermissoesAcessoSelector from '@/components/base/permissoes/PermissoesAcessoSelector.vue'

const metadataStore = useMetadataListasStore()
const authStore = useAuthStore()
const listasIa = reactive({})

// --- Estado para Listas Vinculadas a Clientes ---
const mostrarModalBuscaListaCliente = ref(false)
const listasClienteCache = ref([])
const configBuscaListaCliente = computed(() => {
  const idEmpresa = item.value?.idEmpresa || authStore.empresaAtiva
  return {
    titulo: 'Pesquisar Lista Personalizada',
    endpointApi: 'ListasCliente',
    idField: 'prefixoItemLista',
    camposFiltro: [
      { model: 'nomeLista', label: 'Nome da Lista', tipo: 'text' },
      { model: 'idEmpresa', label: 'Empresa', tipo: 'hidden', value: idEmpresa }
    ],
    colunasGrid: [
      { header: 'Prefixo', dataField: 'prefixoItemLista', width: '30%' },
      { header: 'Nome da Lista', dataField: 'nomeLista', width: '55%' },
      { header: 'Descrição', dataField: 'descricaoLista', width: '15%' }
    ]
  }
})

function obterNomeListaCliente (prefixo) {
  const encontrada = listasClienteCache.value.find(l => l.prefixoItemLista === prefixo)
  return encontrada ? encontrada.nomeLista : prefixo
}

async function abrirModalBuscaListaCliente () {
  if (listasClienteCache.value.length === 0) {
    try {
      const res = await api.get('/ListasCliente?pageSize=1000')
      listasClienteCache.value = res.data?.items || res.data || []
    } catch (e) {
      console.warn('Erro ao carregar listas de cliente:', e)
    }
  }
  mostrarModalBuscaListaCliente.value = true
}

function onListaClienteSelecionada (itemSelecionado) {
  if (!item.value.listasVinculadasClientes) {
    item.value.listasVinculadasClientes = []
  }
  const prefixo = itemSelecionado.prefixoItemLista || itemSelecionado.idListaSistemaItem || itemSelecionado.id
  if (!item.value.listasVinculadasClientes.includes(prefixo)) {
    item.value.listasVinculadasClientes.push(prefixo)
  }
  // Adiciona ao cache para exibir o nome
  if (!listasClienteCache.value.find(l => l.prefixoItemLista === prefixo)) {
    listasClienteCache.value.push(itemSelecionado)
  }
  mostrarModalBuscaListaCliente.value = false
}

function removerListaCliente (idx) {
  if (item.value.listasVinculadasClientes) {
    item.value.listasVinculadasClientes.splice(idx, 1)
  }
}

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idEmpresa: 0,
      nomeEmpresa: '',
      idPlanoVigente: null,
      validadeContrato: null,
      quantidadeUsuariosimultaneos: 0,
      incluirCategoriasComerciais: true,
      // GUARDIÃO: Chaves lógicas das autoridades
      idAutoridadeTributariaFederal: null,
      idAutoridadeTributariaIbs: null,
      lstMoedaPadrao: null,
      lstMoedaGerencial: null,
      tokenExpiracaoMinutos: null,
      // API Key de IA da empresa (compartilhada)
      apiKeyIa: '',
      apiKeyIaMasked: '',
      // Configuração de numeração de ordens de compra
      configTemplateNumeroOrdem: null,
      // Configuração de numeração de pedidos de venda
      configTemplateNumeroPedidoVenda: null,
      // Configuração de numeração de proforma invoice
      configTemplateNumeroProformaInvoice: null,
      // Configuração de numeração de commercial invoice
      configTemplateNumeroCommercialInvoice: null,
      // Configuração de numeração de contrato de venda
      configTemplateNumeroContratoVenda: null,
      // Configuração de numeração de contrato de compra
      configTemplateNumeroContratoCompra: null,
      // Configuração de numeração de purchase order
      configTemplateNumeroPurchaseOrder: null,
      // Margem Esperada Padrão
      margemEsperadaPercentual: null,
      // Listas vinculadas para atributos de clientes
      listasVinculadasClientes: []
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'changesDetected', 'show-notification'])

// GUARDIÃO: Opções Padrão para CampoSelect Sim/Não
const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const accordionState = reactive({
  dadosBasicos: true,
  permissoes: false,
  plano: false,
  autoridades: false,
  financeiro: false,
  templates: false,
  configuracoesComerciais: false,
  parametrosComissaoPapel: false,
  configuracoesIA: false,
  tokenJwt: false,
  configuracoesCompras: false,
  templatesRelatorio: false
})

// --- PARÂMETROS DE COMISSÃO POR PAPEL COMERCIAL ---

const parametrosComissao = ref([])
const isLoadingParametrosComissao = ref(false)

const carregarParametrosComissao = async () => {
  if (!item.value.idEmpresa) return
  isLoadingParametrosComissao.value = true
  try {
    const response = await api.get(`/EmpresaParametrosComissaoPapel/empresa/${item.value.idEmpresa}`)
    parametrosComissao.value = response.data || []
  } catch (error) {
    console.error('Erro ao carregar parâmetros de comissão:', error)
    emit('show-notification', { message: 'Erro ao carregar parâmetros de comissão.', type: 'error' })
  } finally {
    isLoadingParametrosComissao.value = false
  }
}

const salvarParametroComissao = async (param) => {
  if (!param.idEmpresaParametroComissaoPapel) return
  try {
    await api.put(`/EmpresaParametrosComissaoPapel/${param.idEmpresaParametroComissaoPapel}`, {
      percentualComissao: param.percentualComissao,
      geraComissaoCascata: param.geraComissaoCascata,
      isAtivo: param.isAtivo
    })
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao salvar parâmetro de comissão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const sincronizarPapeisComissao = async () => {
  if (!item.value.idEmpresa) return
  isLoadingParametrosComissao.value = true
  try {
    const response = await api.post(`/EmpresaParametrosComissaoPapel/empresa/${item.value.idEmpresa}/sincronizar`)
    emit('show-notification', { message: response.data.mensagem, type: 'success' })
    await carregarParametrosComissao()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao sincronizar papéis de comissão.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingParametrosComissao.value = false
  }
}

// --- PARÂMETROS COMERCIAIS (flag IsAprovacaoAutomatica) ---

const parametrosComerciais = reactive({
  idEmpresaParametrosComerciais: 0,
  idEmpresa: 0,
  isAprovacaoAutomatica: false
})

const isLoadingParametrosComerciais = ref(false)

const aplicarDadosParametrosComerciais = (dados) => {
  if (!dados) return
  Object.assign(parametrosComerciais, {
    idEmpresaParametrosComerciais: dados.idEmpresaParametrosComerciais,
    idEmpresa: dados.idEmpresa,
    isAprovacaoAutomatica: !!dados.isAprovacaoAutomatica
  })
}

const criarParametrosComerciais = async () => {
  const response = await api.post(`/EmpresaParametrosComerciais/empresa/${item.value.idEmpresa}`)
  aplicarDadosParametrosComerciais(response.data)
}

const carregarParametrosComerciais = async () => {
  if (!item.value.idEmpresa) return
  isLoadingParametrosComerciais.value = true
  try {
    const response = await api.get(`/EmpresaParametrosComerciais/empresa/${item.value.idEmpresa}`)
    aplicarDadosParametrosComerciais(response.data)
  } catch (error) {
    // 404 = empresa criada antes desta feature (sem registro na tabela).
    // Cria automaticamente via POST idempotente para que o GET volte a funcionar.
    if (error.response?.status === 404) {
      try {
        await criarParametrosComerciais()
      } catch (e) {
        console.error('Erro ao criar parâmetros comerciais (empresa legada):', e)
        emit('show-notification', { message: 'Erro ao preparar parâmetros comerciais.', type: 'error' })
      }
    } else {
      console.error('Erro ao carregar parâmetros comerciais:', error)
      emit('show-notification', { message: 'Erro ao carregar parâmetros comerciais.', type: 'error' })
    }
  } finally {
    isLoadingParametrosComerciais.value = false
  }
}

const salvarParametrosComerciais = async () => {
  if (!item.value.idEmpresa) return

  // Se ainda não existe registro (empresa legada), cria antes de atualizar
  if (!parametrosComerciais.idEmpresaParametrosComerciais) {
    try {
      await criarParametrosComerciais()
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao criar parâmetros comerciais.'
      emit('show-notification', { message: msg, type: 'error' })
      return
    }
  }

  try {
    await api.put(`/EmpresaParametrosComerciais/${parametrosComerciais.idEmpresaParametrosComerciais}`, {
      isAprovacaoAutomatica: parametrosComerciais.isAprovacaoAutomatica
    })
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao salvar parâmetros comerciais.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- LÓGICA DE TEMPLATES DE RELATÓRIO ---

const templatesRelatorio = ref([])
const templatesRelatorioHabilitados = ref([])
const isLoadingTemplatesRelatorio = ref(false)
const showPreviewModal = ref(false)
const previewTemplates = ref([])
const imagensEmpresa = ref([])

// --- NAVEGAÇÃO MASTER-DETAIL DOS TEMPLATES DE RELATÓRIO ---
const mostrarModalTemplatesRelatorio = ref(false)
const moduloSelecionado = ref(null)
const templateSelecionadoId = ref(null)

// Agrupa os templates disponíveis por CodigoModulo (a "aplicação"/necessidade),
// com contagem de quantos designs estão habilitados para a empresa.
const aplicacoes = computed(() => {
  const mapa = {}
  for (const tmpl of templatesRelatorio.value) {
    const cod = tmpl.codigoModulo || 'OUTROS'
    if (!mapa[cod]) mapa[cod] = { codigoModulo: cod, templates: [] }
    const hab = templatesRelatorioHabilitados.value.find(
      h => String(h.idRelatorioTemplate) === String(tmpl.idRelatorioTemplate)
    )
    const habilitado = !!hab
    let slotsImagem = []
    try {
      slotsImagem = tmpl.slotsImagemJson ? JSON.parse(tmpl.slotsImagemJson) : []
    } catch (e) { slotsImagem = [] }
    let imagensConfig = {}
    try {
      imagensConfig = hab?.configuracaoImagensJson ? JSON.parse(hab.configuracaoImagensJson) : {}
    } catch (e) { imagensConfig = {} }
    mapa[cod].templates.push({ ...tmpl, habilitado, slotsImagem, imagensConfig })
  }
  return Object.values(mapa).map(m => ({
    ...m,
    total: m.templates.length,
    habilitados: m.templates.filter(t => t.habilitado).length
  }))
})

const templatesDoModulo = computed(() =>
  aplicacoes.value.find(a => a.codigoModulo === moduloSelecionado.value)?.templates || []
)

// Configuração de busca do repositório de imagens da empresa (para os slots dos templates)
const configBuscaImagem = computed(() => criarConfigBuscaEmpresaImagem(item.value.idEmpresa))

const templateSelecionado = computed(() =>
  templatesDoModulo.value.find(t => t.idRelatorioTemplate === templateSelecionadoId.value) || null
)

const totalTemplatesHabilitados = computed(() => templatesRelatorioHabilitados.value.length)

// Aplicações ordenadas alfabeticamente pelo código do módulo (aplicação)
const aplicacoesOrdenadas = computed(() =>
  [...aplicacoes.value].sort((a, b) => a.codigoModulo.localeCompare(b.codigoModulo))
)

const tituloModalTemplatesRelatorio = computed(() =>
  moduloSelecionado.value ? `Templates de Relatório — ${moduloSelecionado.value}` : 'Templates de Relatório'
)

const abrirModalTemplatesRelatorio = (codigoModulo) => {
  moduloSelecionado.value = codigoModulo || aplicacoes.value[0]?.codigoModulo || null
  templateSelecionadoId.value = null
  mostrarModalTemplatesRelatorio.value = true
}

const selecionarTemplate = (tmpl) => {
  templateSelecionadoId.value = tmpl.idRelatorioTemplate
}

async function buscarTemplatesRelatorio () {
  if (!item.value?.idEmpresa) return
  isLoadingTemplatesRelatorio.value = true
  try {
    const [disponiveisRes, habilitadosRes, imagensRes] = await Promise.all([
      api.get(`/RelatorioTemplates/disponiveis-empresa/${item.value.idEmpresa}`),
      api.get(`/EmpresaRelatorioTemplates/empresa/${item.value.idEmpresa}`),
      api.get(`/EmpresaImagens/empresa/${item.value.idEmpresa}`)
    ])
    templatesRelatorio.value = disponiveisRes.data || []
    templatesRelatorioHabilitados.value = habilitadosRes.data || []
    imagensEmpresa.value = imagensRes.data || []
  } catch (error) {
    console.error('Erro ao carregar templates de relatório:', error)
    emit('show-notification', { message: 'Erro ao carregar templates de relatório.', type: 'error' })
  } finally {
    isLoadingTemplatesRelatorio.value = false
  }
}

async function toggleTemplateRelatorio (tmpl) {
  if (!item.value?.idEmpresa) return
  try {
    if (tmpl.habilitado) {
      await api.delete(`/EmpresaRelatorioTemplates/${item.value.idEmpresa}/${tmpl.idRelatorioTemplate}`)
      const idx = templatesRelatorioHabilitados.value.findIndex(
        h => String(h.idRelatorioTemplate) === String(tmpl.idRelatorioTemplate)
      )
      if (idx >= 0) templatesRelatorioHabilitados.value.splice(idx, 1)
      emit('show-notification', { message: `Template "${tmpl.nome}" desabilitado.`, type: 'info' })
    } else {
      const response = await api.post('/EmpresaRelatorioTemplates', {
        idEmpresa: item.value.idEmpresa,
        idRelatorioTemplate: tmpl.idRelatorioTemplate,
        lstStatus: 'ST1',
        configuracaoImagensJson: '{}'
      })
      if (response.data) {
        templatesRelatorioHabilitados.value.push(response.data)
      }
      emit('show-notification', { message: `Template "${tmpl.nome}" habilitado.`, type: 'success' })
    }
  } catch (error) {
    console.error('Erro ao alternar template:', error)
    const msg = error.response?.data?.message || 'Erro ao alternar template de relatório.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

function obterIdImagemSlot (tmpl, chaveSlot) {
  const hab = templatesRelatorioHabilitados.value.find(
    h => String(h.idRelatorioTemplate) === String(tmpl.idRelatorioTemplate)
  )
  if (!hab?.configuracaoImagensJson) return null
  try {
    const config = JSON.parse(hab.configuracaoImagensJson)
    return config[chaveSlot] || null
  } catch (e) {
    return null
  }
}

function obterImagemSlot (tmpl, chaveSlot) {
  const idImagem = obterIdImagemSlot(tmpl, chaveSlot)
  if (!idImagem) return null
  return imagensEmpresa.value.find(img => Number(img.idEmpresaImagem) === Number(idImagem)) || null
}

function formatarTamanhoArquivo (bytes) {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function atualizarImagemSlot (tmpl, chaveSlot, idImagem) {
  const hab = templatesRelatorioHabilitados.value.find(
    h => String(h.idRelatorioTemplate) === String(tmpl.idRelatorioTemplate)
  )
  if (!hab) return
  try {
    const config = hab.configuracaoImagensJson ? JSON.parse(hab.configuracaoImagensJson) : {}
    config[chaveSlot] = idImagem ? Number(idImagem) : null
    hab.configuracaoImagensJson = JSON.stringify(config)
    // Se o template já está habilitado (possui IdEmpresaRelatorioTemplate), usa PUT para atualizar
    // Caso contrário, usa POST para criar o registro
    if (hab.idEmpresaRelatorioTemplate) {
      await api.put(`/EmpresaRelatorioTemplates/${item.value.idEmpresa}/${tmpl.idRelatorioTemplate}`, {
        idEmpresa: item.value.idEmpresa,
        idRelatorioTemplate: tmpl.idRelatorioTemplate,
        lstStatus: hab.lstStatus || 'ST1',
        configuracaoImagensJson: hab.configuracaoImagensJson
      })
    } else {
      await api.post('/EmpresaRelatorioTemplates', {
        idEmpresa: item.value.idEmpresa,
        idRelatorioTemplate: tmpl.idRelatorioTemplate,
        lstStatus: hab.lstStatus || 'ST1',
        configuracaoImagensJson: hab.configuracaoImagensJson
      })
    }
  } catch (error) {
    console.error('Erro ao atualizar imagem do slot:', error)
    emit('show-notification', { message: 'Erro ao salvar configuração de imagem.', type: 'error' })
  }
}

// --- VALIDAÇÃO DE PROPORÇÃO DOS SLOTS DE IMAGEM ---
// A proporção exigida pelo slot é derivada de maxWidth:maxHeight (não há
// validação pelos valores máximos de pixel, apenas pela proporção).
const proporcaoResetKeys = reactive({})

const validarProporcaoEAtualizarImagem = async (img, tmpl, slot) => {
  if (!img) return
  if (!proporcaoAtende(img.larguraOriginal, img.alturaOriginal, slot.maxWidth, slot.maxHeight)) {
    emit('show-notification', {
      message: `A imagem "${img.nomeAmigavel || img.nomeArquivoOriginal}" não atende à proporção exigida (${formatarProporcao(slot.maxWidth, slot.maxHeight)}).`,
      type: 'warning'
    })
    proporcaoResetKeys[slot.chave] = (proporcaoResetKeys[slot.chave] || 0) + 1
    return
  }
  await atualizarImagemSlot(tmpl, slot.chave, img.idEmpresaImagem)
}

const aoLimparImagemSlot = (val, tmpl, slot) => {
  if (val === null || val === undefined || val === '') {
    atualizarImagemSlot(tmpl, slot.chave, null)
  }
}

async function previewTemplateRelatorio (tmpl) {
  previewTemplates.value = [tmpl]
  showPreviewModal.value = true
}

function fecharPreviewRelatorio () {
  showPreviewModal.value = false
  previewTemplates.value = []
}

// --- LÓGICA DE TEMPLATES DE NUMERAÇÃO (modal compartilhado) ---

const templatePedidoVenda = ref([])
const templateProformaInvoice = ref([])
const templateCommercialInvoice = ref([])
const templateContratoVenda = ref([])
const templateContratoCompra = ref([])
const templateOrdemCompra = ref([])
const templatePurchaseOrder = ref([])

const parseTemplateNumeracao = (json) => {
  try {
    return json ? JSON.parse(json) : []
  } catch (e) {
    return []
  }
}

const gerarPreviewTemplate = (template) => {
  if (!template || template.length === 0) return ''
  const hoje = dataHojeBR().split('-')
  const ano = hoje[0]
  const mes = hoje[1]
  const dia = hoje[2]
  return template.map(parte => {
    switch (parte.tipo) {
      case 'prefixo': return parte.valor || ''
      case 'separador': return parte.valor || ''
      case 'ano4': return ano
      case 'ano2': return ano.slice(-2)
      case 'mes': return mes
      case 'dia': return dia
      case 'idFilial': return '01'
      case 'sequencial': return String(1).padStart(parte.digitos || 5, '0')
      default: return ''
    }
  }).join('')
}

const previewTemplatePedidoVenda = computed(() => gerarPreviewTemplate(templatePedidoVenda.value))
const previewTemplateProformaInvoice = computed(() => gerarPreviewTemplate(templateProformaInvoice.value))
const previewTemplateCommercialInvoice = computed(() => gerarPreviewTemplate(templateCommercialInvoice.value))
const previewTemplateContratoVenda = computed(() => gerarPreviewTemplate(templateContratoVenda.value))
const previewTemplateContratoCompra = computed(() => gerarPreviewTemplate(templateContratoCompra.value))
const previewTemplateOrdemCompra = computed(() => gerarPreviewTemplate(templateOrdemCompra.value))
const previewTemplatePurchaseOrder = computed(() => gerarPreviewTemplate(templatePurchaseOrder.value))

// --- MODAL DE EDIÇÃO DE TEMPLATE DE NUMERAÇÃO (compartilhado) ---

const modalTemplateNumeracao = reactive({
  visivel: false,
  titulo: '',
  tipo: '', // 'pedidoVenda' | 'proformaInvoice' | 'commercialInvoice' | 'contratoVenda' | 'contratoCompra' | 'ordemCompra' | 'purchaseOrder'
  partes: [],
  sequencialNaoFinal: false,
  preview: ''
})

const abrirModalTemplateNumeracao = (tipo) => {
  const mapTitulos = {
    pedidoVenda: 'Configurar Numeração - Pedido de Venda',
    proformaInvoice: 'Configurar Numeração - Proforma Invoice',
    commercialInvoice: 'Configurar Numeração - Commercial Invoice',
    contratoVenda: 'Configurar Numeração - Contrato de Venda',
    contratoCompra: 'Configurar Numeração - Contrato de Compra',
    ordemCompra: 'Configurar Numeração - Ordem de Compra',
    purchaseOrder: 'Configurar Numeração - Purchase Order'
  }
  const mapTemplates = {
    pedidoVenda: templatePedidoVenda,
    proformaInvoice: templateProformaInvoice,
    commercialInvoice: templateCommercialInvoice,
    contratoVenda: templateContratoVenda,
    contratoCompra: templateContratoCompra,
    ordemCompra: templateOrdemCompra,
    purchaseOrder: templatePurchaseOrder
  }

  modalTemplateNumeracao.tipo = tipo
  modalTemplateNumeracao.titulo = mapTitulos[tipo] || 'Configurar Numeração'
  // Clonar o array para edição no modal
  modalTemplateNumeracao.partes = JSON.parse(JSON.stringify(mapTemplates[tipo].value))
  // Atualizar preview e validação com os dados carregados
  atualizarPreviewModalNumeracao()
  modalTemplateNumeracao.visivel = true
}

const adicionarParteTemplateNumeracao = () => {
  modalTemplateNumeracao.partes.push({
    tipo: 'separador',
    valor: '-',
    digitos: null
  })
  atualizarPreviewModalNumeracao()
}

const removerParteTemplateNumeracao = (index) => {
  modalTemplateNumeracao.partes.splice(index, 1)
  atualizarPreviewModalNumeracao()
}

const moverParteTemplateNumeracao = (index, direcao) => {
  const novoIndex = index + direcao
  if (novoIndex < 0 || novoIndex >= modalTemplateNumeracao.partes.length) return
  const temp = modalTemplateNumeracao.partes[index]
  modalTemplateNumeracao.partes[index] = modalTemplateNumeracao.partes[novoIndex]
  modalTemplateNumeracao.partes[novoIndex] = temp
  atualizarPreviewModalNumeracao()
}

const aoAlterarTipoParteNumeracao = (index) => {
  const parte = modalTemplateNumeracao.partes[index]
  if (parte.tipo === 'prefixo') {
    parte.valor = parte.valor || ''
    parte.digitos = null
  } else if (parte.tipo === 'separador') {
    parte.valor = parte.valor || '-'
    parte.digitos = null
  } else if (parte.tipo === 'sequencial') {
    parte.valor = null
    parte.digitos = parte.digitos || 5
  } else {
    parte.valor = null
    parte.digitos = null
  }
  atualizarPreviewModalNumeracao()
}

const atualizarPreviewModalNumeracao = () => {
  const partes = modalTemplateNumeracao.partes
  const idxSequencial = partes.findIndex(p => p.tipo === 'sequencial')
  modalTemplateNumeracao.sequencialNaoFinal = idxSequencial >= 0 && idxSequencial !== partes.length - 1
  modalTemplateNumeracao.preview = gerarPreviewTemplate(partes)
}

const confirmarTemplateNumeracao = () => {
  const mapTemplates = {
    pedidoVenda: templatePedidoVenda,
    proformaInvoice: templateProformaInvoice,
    commercialInvoice: templateCommercialInvoice,
    contratoVenda: templateContratoVenda,
    contratoCompra: templateContratoCompra,
    ordemCompra: templateOrdemCompra,
    purchaseOrder: templatePurchaseOrder
  }
  const mapItemFields = {
    pedidoVenda: 'configTemplateNumeroPedidoVenda',
    proformaInvoice: 'configTemplateNumeroProformaInvoice',
    commercialInvoice: 'configTemplateNumeroCommercialInvoice',
    contratoVenda: 'configTemplateNumeroContratoVenda',
    contratoCompra: 'configTemplateNumeroContratoCompra',
    ordemCompra: 'configTemplateNumeroOrdem',
    purchaseOrder: 'configTemplateNumeroPurchaseOrder'
  }

  const tipo = modalTemplateNumeracao.tipo
  const partes = modalTemplateNumeracao.partes

  // Validar
  if (partes.length > 0) {
    const idxSequencial = partes.findIndex(p => p.tipo === 'sequencial')
    if (idxSequencial < 0) {
      emit('show-notification', { message: 'O template deve conter uma parte do tipo "Sequencial".', type: 'warning' })
      return
    }
    if (idxSequencial !== partes.length - 1) {
      emit('show-notification', { message: 'O "Sequencial" deve ser sempre o último elemento do template.', type: 'warning' })
      return
    }
  }

  // Atualizar o template reativo
  mapTemplates[tipo].value = JSON.parse(JSON.stringify(partes))

  // Serializar para o item (enviar ao backend)
  if (partes.length > 0) {
    item.value[mapItemFields[tipo]] = JSON.stringify(partes)
  } else {
    item.value[mapItemFields[tipo]] = null
  }

  modalTemplateNumeracao.visivel = false
}

// --- LÓGICA DE IMAGENS removida para RepositorioImagensEmpresa.vue ---
const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel,
  initializeForm
} = useFormulario(props, emit, { idKey: 'idEmpresa' })

// ============================================================
// ESTADO DE SALVAMENTO (Salvar e Continuar)
// ============================================================
const isEmpresaSaved = ref(isEditing.value)
const isSavingEmpresaLocal = ref(false)
const isFramesSecundariosBloqueados = computed(() => !isEmpresaSaved.value || isLoading.value)

// ============================================================
// FUNÇÃO: Salvar Empresa Localmente (criação inicial)
// ============================================================
const salvarEmpresaLocalmente = async () => {
  if (isSavingEmpresaLocal.value) return

  if (!item.value.nomeEmpresa) {
    emit('show-notification', { message: 'O campo Nome da Empresa é obrigatório.', type: 'warning' })
    return
  }

  isSavingEmpresaLocal.value = true
  const payload = {
    ...item.value,
    incluirCategoriasComerciais: item.value.incluirCategoriasComerciais !== false,
    listasVinculadasClientes: item.value.listasVinculadasClientes || []
  }

  try {
    const response = await api.post('/Empresas', payload)
    item.value = response.data
    isEmpresaSaved.value = true
    isEditing.value = true
    entityInfo.value = { id: item.value.idEmpresa, name: item.value.nomeEmpresa, nameLabel: 'Empresa' }
    emit('show-notification', { message: 'Empresa salva. Agora pode configurar permissões e módulos.', type: 'success' })
    accordionState.permissoes = true
    accordionState.plano = true
    carregarParametrosComissao()
    carregarParametrosComerciais()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar a empresa.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingEmpresaLocal.value = false
  }
}

// ============================================================
// FUNÇÃO: Salvar Alterações (via @submit-continuar)
// ============================================================
const salvarAlteracoes = async () => {
  if (isLoading.value || isLoadingPermissoes.value || !isEmpresaSaved.value) return

  const payload = {
    ...item.value,
    idEmpresa: item.value.idEmpresa
  }

  try {
    const res = await api.put(`/Empresas/${item.value.idEmpresa}`, payload)
    if (res?.data) {
      initializeForm(res.data)
    }
    // Salvar permissões via endpoint dedicado
    if (item.value.idEmpresa) {
      await api.post(`/EmpresasPermissoesAcesso/bulk/${item.value.idEmpresa}`,
        permissoesSelecionadasIds.value.map(id => ({ idRegraAcessoPermissao: id }))
      )
    }
    // Salvar parâmetros comerciais (flag IsAprovacaoAutomatica)
    await salvarParametrosComerciais()
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
    carregarParametrosComissao()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const listas = reactive({})

// --- LÓGICA DE TEMPLATES DE PRECIFICAÇÃO ---

const templates = ref([])
const modalTemplate = reactive({
  visivel: false,
  atributos: [],
  item: {
    idTemplatePrecificacao: 0,
    idEmpresa: 0,
    nome: '',
    estruturaJsonSchema: '',
    lstStatus: ST.Ativo
  }
})

const parseJson = (json) => {
  try {
    return json ? JSON.parse(json) : []
  } catch (e) {
    return []
  }
}

const buscarTemplates = async () => {
  if (!isEditing.value) return
  try {
    const response = await api.get(`/TemplatesPrecificacao?idEmpresa=${item.value.idEmpresa}`)
    templates.value = response.data
  } catch (error) {
    console.error('Erro ao buscar templates:', error)
  }
}

const novoTemplate = () => {
  modalTemplate.atributos = []
  modalTemplate.item = {
    idTemplatePrecificacao: 0,
    idEmpresa: item.value.idEmpresa,
    nome: '',
    estruturaJsonSchema: '',
    lstStatus: ST.Ativo
  }
  modalTemplate.visivel = true
}

const editarTemplate = (temp) => {
  modalTemplate.item = { ...temp }
  modalTemplate.atributos = parseJson(temp.estruturaJsonSchema)
  modalTemplate.visivel = true
}

const adicionarAtributo = () => {
  modalTemplate.atributos.push({ chave: '', label: '' })
}

const removerAtributo = (index) => {
  modalTemplate.atributos.splice(index, 1)
}

const salvarTemplate = async () => {
  try {
    // Validar e formatar JSON
    if (modalTemplate.atributos.length > 0) {
      modalTemplate.item.estruturaJsonSchema = JSON.stringify(modalTemplate.atributos)
    } else {
      modalTemplate.item.estruturaJsonSchema = null
    }

    if (modalTemplate.item.idTemplatePrecificacao) {
      await api.put(`/TemplatesPrecificacao/${modalTemplate.item.idTemplatePrecificacao}`, modalTemplate.item)
    } else {
      await api.post('/TemplatesPrecificacao', modalTemplate.item)
    }
    modalTemplate.visivel = false
    buscarTemplates()
    emit('show-notification', { message: 'Template salvo com sucesso!', type: 'success' })
  } catch (error) {
    console.error('Erro ao salvar template:', error)
    emit('show-notification', { message: 'Erro ao salvar o template.', type: 'danger' })
  }
}

const excluirTemplate = async (id) => {
  if (!confirm('Deseja realmente excluir este template?')) return
  try {
    await api.delete(`/TemplatesPrecificacao/${id}`)
    buscarTemplates()
    emit('show-notification', { message: 'Template excluído com sucesso!', type: 'success' })
  } catch (error) {
    console.error('Erro ao excluir template:', error)
    emit('show-notification', { message: 'Erro ao excluir o template.', type: 'danger' })
  }
}

// --- LÓGICA DAS AUTORIDADES TRIBUTÁRIAS ---

const confBuscaAutoridadeFederal = computed(() =>
  criarConfigBuscaParceiroAutoridadeTributaria(item.value?.idEmpresa, 'PARCEIRO_ESFERA_GOV2')
)

const confBuscaAutoridadeIbs = computed(() =>
  criarConfigBuscaParceiroAutoridadeTributaria(item.value?.idEmpresa, PARCEIRO_ESFERA_GOV.ComiteGestor)
)

const mostrarAuditoria = ref(false)

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

// --- LÓGICA DE PERMISSÕES DE ACESSO (via PermissoesAcessoSelector) ---

const permissoesConsolidadas = ref([])
const permissoesSelecionadasIds = ref([])
const isLoadingPermissoes = ref(false)
const erroPermissoes = ref(null)
const confirmacaoLiberarPermissoesRef = ref(null)

const buscarPermissoesConsolidadas = async () => {
  isLoadingPermissoes.value = true
  erroPermissoes.value = null
  try {
    const response = await api.get('/RegraAcessoPermissao/catalogo')
    permissoesConsolidadas.value = Array.isArray(response.data) ? response.data : (response.data?.items || [])
  } catch (error) {
    console.error('Erro ao buscar catálogo de permissões:', error)
    erroPermissoes.value = 'Falha ao carregar a matriz de permissões do sistema.'
  } finally {
    isLoadingPermissoes.value = false
  }
}

const buscarPermissoesDaEmpresa = async (idEmpresa) => {
  console.log(`[DEBUG EmpresasCadastro] buscarPermissoesDaEmpresa chamado com idEmpresa=${idEmpresa}, isEditing=${isEditing.value}`)
  if (!idEmpresa) {
    console.warn('[DEBUG EmpresasCadastro] idEmpresa é falsy, abortando')
    return
  }
  try {
    const response = await api.get(`/EmpresasPermissoesAcesso/empresa/${idEmpresa}`)
    console.log('[DEBUG EmpresasCadastro] Resposta da API:', response.data)
    console.log('[DEBUG EmpresasCadastro] Primeiro item:', response.data?.[0])
    console.log('[DEBUG EmpresasCadastro] Keys do primeiro item:', response.data?.[0] ? Object.keys(response.data[0]) : 'N/A')
    const vinculos = Array.isArray(response.data) ? response.data : []
    permissoesSelecionadasIds.value = vinculos.map(v => v.idRegraAcessoPermissao)
    console.log('[DEBUG EmpresasCadastro] permissoesSelecionadasIds atualizado (length):', permissoesSelecionadasIds.value.length)
  } catch (error) {
    console.error(`Erro ao buscar permissões da empresa ${idEmpresa}:`, error)
    emit('show-notification', { message: 'Erro ao carregar permissões associadas a esta empresa.', type: 'error' })
    permissoesSelecionadasIds.value = []
  }
}

const confirmarLiberarTodasPermissoes = () => {
  confirmacaoLiberarPermissoesRef.value?.show()
}

const liberarTodasPermissoes = async (confirmed) => {
  if (!confirmed) return
  const idEmpresa = item.value.idEmpresa
  if (!idEmpresa) return

  try {
    // Coleta todos os IDs de permissão disponíveis
    const todosIds = []
    for (const item of permissoesConsolidadas.value) {
      if (item && item.regras) {
        for (const regra of item.regras) {
          if (regra && regra.permissoes) {
            regra.permissoes.forEach(p => todosIds.push(p.idRegraAcessoPermissao))
          }
        }
      }
    }
    await api.post(`/EmpresasPermissoesAcesso/bulk/${idEmpresa}`,
      todosIds.map(id => ({ idRegraAcessoPermissao: id }))
    )
    permissoesSelecionadasIds.value = todosIds
    emit('show-notification', { message: 'Todas as permissões liberadas com sucesso!', type: 'success' })
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao liberar permissões.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- ESTADO DO RELATÓRIO DE DIAGNÓSTICO ---
const mostrarRelatorio = ref(false)
const dadosRelatorio = reactive({
  modoMultiEmpresa: false,
  nivelAntigo: '',
  nivelNovo: '',
  empresasComPendencias: [],
  pendencias: [],
  podeFazerUpgrade: false,
  totalPendencias: 0,
  isLoading: false
})

const aoFecharModal = (valor) => {
  mostrarRelatorio.value = valor
}

const salvarEmpresa = async () => {
  // GUARDIÃO: Verificar se o plano mudou para um nível fiscal superior
  if (isEditing.value && item.value.idPlanoVigente && props.itemParaEditar?.idPlanoVigente &&
      item.value.idPlanoVigente !== props.itemParaEditar.idPlanoVigente) {
    dadosRelatorio.isLoading = true
    try {
      // Buscar níveis fiscais dos planos
      const planoAntigoResp = await api.get(`/Planos/${props.itemParaEditar.idPlanoVigente}`)
      const planoNovoResp = await api.get(`/Planos/${item.value.idPlanoVigente}`)
      const nivelAntigo = planoAntigoResp.data.lstNivelModuloFiscal
      const nivelNovo = planoNovoResp.data.lstNivelModuloFiscal

      // Se o nível mudou, executar diagnóstico
      if (nivelAntigo !== nivelNovo && item.value.idEmpresa) {
        dadosRelatorio.nivelAntigo = nivelAntigo
        dadosRelatorio.nivelNovo = nivelNovo

        const diagResp = await api.get(`/Fiscal/diagnostico/${item.value.idEmpresa}`)
        const diag = diagResp.data

        dadosRelatorio.pendencias = diag.pendencias || []
        dadosRelatorio.totalPendencias = diag.totalPendencias || 0
        dadosRelatorio.podeFazerUpgrade = diag.podeFazerUpgrade || false
        dadosRelatorio.modoMultiEmpresa = false

        if (!diag.podeFazerUpgrade) {
          dadosRelatorio.isLoading = false
          emit('show-notification', {
            message: `Upgrade bloqueado. ${diag.totalPendencias || 0} pendência(s) encontrada(s).`,
            type: 'warning'
          })
          mostrarRelatorio.value = true
          return // BLOQUEIA o salvamento
        }
      }
    } catch (e) {
      console.error('Erro ao verificar diagnóstico de upgrade:', e)
    } finally {
      dadosRelatorio.isLoading = false
    }
  }

  // Serializar templates de numeração (usando o modal compartilhado)
  const serializarTemplate = (templateRef, fieldName) => {
    if (templateRef.value.length > 0) {
      item.value[fieldName] = JSON.stringify(templateRef.value)
    } else {
      item.value[fieldName] = null
    }
  }

  serializarTemplate(templateOrdemCompra, 'configTemplateNumeroOrdem')
  serializarTemplate(templatePedidoVenda, 'configTemplateNumeroPedidoVenda')
  serializarTemplate(templateProformaInvoice, 'configTemplateNumeroProformaInvoice')
  serializarTemplate(templateCommercialInvoice, 'configTemplateNumeroCommercialInvoice')
  serializarTemplate(templateContratoVenda, 'configTemplateNumeroContratoVenda')
  serializarTemplate(templateContratoCompra, 'configTemplateNumeroContratoCompra')
  serializarTemplate(templatePurchaseOrder, 'configTemplateNumeroPurchaseOrder')

  const payloadFinal = {
    ...item.value,
    listasVinculadasClientes: item.value.listasVinculadasClientes || []
  }

  await submitForm('Empresas', 'idEmpresa', payloadFinal)

  // Salvar permissões via endpoint dedicado
  if (item.value.idEmpresa) {
    try {
      await api.post(`/EmpresasPermissoesAcesso/bulk/${item.value.idEmpresa}`,
        permissoesSelecionadasIds.value.map(id => ({ idRegraAcessoPermissao: id }))
      )
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao salvar permissões de acesso.'
      emit('show-notification', { message: msg, type: 'error' })
    }
  }

  // Salvar parâmetros comerciais (flag IsAprovacaoAutomatica)
  await salvarParametrosComerciais()
}

watch(() => permissoesSelecionadasIds.value, () => {
  if (!isLoadingPermissoes.value && isEditing.value) {
    emit('changes-detected', true)
  }
}, { deep: true })

const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const obj = lista.find(i =>
    String(i.idListaSistemaItem) === String(id) ||
    String(i.idListaClienteItem) === String(id) ||
    i.textoLista === id
  )
  return obj ? obj.textoLista : id
}

const carregarListas = async () => {
  try {
    // GUARDIÃO: Carrega separadamente para evitar conflito de LstStatus
    // entre o contexto do Template (Cliente) e o da IA (Sistema)
    const resultGeral = await metadataStore.carregarListasParaTabelas(['Empresa', 'ClienteIntegracaoAi'])
    const resultTemplate = await metadataStore.carregarListasParaTabelas(['TemplatePrecificacao', 'TemplatePrecificacao', 'DocumentoEntradaTipo'])
    const resultIaAplicacoes = await metadataStore.carregarListasParaTabelas(['ClienteIntegracaoAiAplicacoes'])

    // listas (template usa contexto Cliente) - template sobrescreve LstStatus
    Object.assign(listas, resultGeral)
    Object.assign(listas, resultTemplate)

    // GUARDIÃO: Para telas de configuração do sistema (ex: cadastro de empresa),
    // o admin não tem tenant context (empresaAtiva) porque está editando/criando
    // a própria empresa. Passamos o idEmpresa explicitamente para o lookup.
    if (isEditing.value && item.value?.idEmpresa) {
      try {
        const stResponse = await api.get('/ListasCliente/lookup/ST', { params: { idEmpresa: item.value.idEmpresa } })
        if (stResponse.data && Array.isArray(stResponse.data)) {
          listas.LstStatus = stResponse.data
        }
      } catch (e) {
        // Mantém o fallback via metadata (resultTemplate já foi assignado acima)
        console.warn('[carregarListas] Não foi possível carregar LstStatus com idEmpresa, mantendo fallback metadata.', e?.response?.status)
      }
    }

    // listasIa (integração IA usa contexto Sistema) - sem template
    Object.assign(listasIa, resultGeral)
    // Aplicações de IA (lista de sistema CONFIG_IA_APLICACOES_SIS)
    if (resultIaAplicacoes.LstAplicacoesAi) {
      listasIa.LstAplicacoesAi = resultIaAplicacoes.LstAplicacoesAi
    }
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares:', e)
  }
}

// --- LÓGICA DE INTEGRAÇÕES DE IA ---

const integracoesAi = ref([])
const confirmacaoIaRef = ref(null)
const exclusaoPendenteIaId = ref(null)
const expandedIaRows = reactive({})

const toggleExpandIntegracao = (id) => {
  expandedIaRows[id] = !expandedIaRows[id]
}

const modalIntegracaoAi = reactive({
  visivel: false,
  salvando: false,
  testando: false,
  resultadoTeste: null,
  aplicacoes: [],
  item: {
    idClienteIntegracaoAi: 0,
    lstProvider: '',
    apiKey: '',
    urlEndpoint: '',
    model: '',
    temperature: null,
    maxTokens: null,
    timeoutSeconds: null,
    enableStreaming: false,
    responseFormat: '',
    lstStatus: ST.Ativo,
    observacoes: ''
  }
})

// Estado do modal de adição de aplicação
const modalIntegracaoAiAplicacao = reactive({
  visivel: false,
  salvando: false,
  item: {
    lstAplicacoesAi: '',
    observacoes: ''
  }
})

const buscarIntegracoesAi = async () => {
  if (!isEditing.value) return
  try {
    const response = await api.get(`/ClienteIntegracoesAi?idEmpresa=${item.value.idEmpresa}`)
    integracoesAi.value = response.data
  } catch (error) {
    console.error('Erro ao buscar integrações de IA:', error)
  }
}

const abrirModalAplicacao = () => {
  modalIntegracaoAiAplicacao.item = {
    lstAplicacoesAi: '',
    observacoes: ''
  }
  modalIntegracaoAiAplicacao.visivel = true
}

const confirmarAdicionarAplicacao = () => {
  if (!modalIntegracaoAiAplicacao.item.lstAplicacoesAi) {
    emit('show-notification', { message: 'Selecione uma aplicação.', type: 'warning' })
    return
  }
  modalIntegracaoAi.aplicacoes.push({
    lstAplicacoesAi: modalIntegracaoAiAplicacao.item.lstAplicacoesAi,
    observacoes: modalIntegracaoAiAplicacao.item.observacoes
  })
  modalIntegracaoAiAplicacao.visivel = false
}

const removerAplicacao = (index) => {
  modalIntegracaoAi.aplicacoes.splice(index, 1)
}

const novaIntegracaoAi = () => {
  modalIntegracaoAi.item = {
    idClienteIntegracaoAi: 0,
    lstProvider: '',
    apiKey: item.value.apiKeyIa || '',
    urlEndpoint: '',
    model: '',
    temperature: null,
    maxTokens: null,
    timeoutSeconds: null,
    enableStreaming: false,
    responseFormat: '',
    lstStatus: ST.Ativo,
    observacoes: ''
  }
  modalIntegracaoAi.aplicacoes = []
  modalIntegracaoAi.resultadoTeste = null
  modalIntegracaoAi.visivel = true
}

const editarIntegracaoAi = (integ) => {
  modalIntegracaoAi.item = {
    idClienteIntegracaoAi: integ.idClienteIntegracaoAi,
    lstProvider: integ.lstProvider,
    apiKey: '',
    urlEndpoint: integ.urlEndpoint || '',
    model: integ.model || '',
    temperature: integ.temperature ?? null,
    maxTokens: integ.maxTokens ?? null,
    timeoutSeconds: integ.timeoutSeconds ?? null,
    enableStreaming: integ.enableStreaming ?? false,
    responseFormat: integ.responseFormat || '',
    lstStatus: integ.lstStatus,
    observacoes: integ.observacoes || ''
  }
  modalIntegracaoAi.aplicacoes = (integ.aplicacoes || []).map(a => ({
    lstAplicacoesAi: a.lstAplicacoesAi || '',
    observacoes: a.observacoes || ''
  }))
  modalIntegracaoAi.resultadoTeste = null
  modalIntegracaoAi.visivel = true
}

const testarConexaoIntegracaoAi = async () => {
  const apiKey = modalIntegracaoAi.item.apiKey || item.value.apiKeyIa
  if (!apiKey || !modalIntegracaoAi.item.lstProvider) return

  modalIntegracaoAi.testando = true
  modalIntegracaoAi.resultadoTeste = null
  try {
    const response = await api.post('/ClienteIntegracoesAi/testar-conexao', {
      lstProvider: modalIntegracaoAi.item.lstProvider,
      apiKey: apiKey,
      urlEndpoint: modalIntegracaoAi.item.urlEndpoint || null,
      model: modalIntegracaoAi.item.model || null
    })
    modalIntegracaoAi.resultadoTeste = {
      sucesso: true,
      mensagem: response.data.mensagem || 'Conexão estabelecida com sucesso!'
    }
  } catch (error) {
    modalIntegracaoAi.resultadoTeste = {
      sucesso: false,
      mensagem: error.response?.data?.message || 'Falha ao testar conexão.'
    }
  } finally {
    modalIntegracaoAi.testando = false
  }
}

const salvarIntegracaoAi = async () => {
  if (!modalIntegracaoAi.item.lstProvider) {
    emit('show-notification', { message: 'Selecione um provedor.', type: 'warning' })
    return
  }
  if (!modalIntegracaoAi.item.idClienteIntegracaoAi && !modalIntegracaoAi.item.apiKey && !item.value.apiKeyIa) {
    emit('show-notification', { message: 'Informe uma API Key ou configure a chave principal da empresa.', type: 'warning' })
    return
  }

  modalIntegracaoAi.salvando = true
  try {
    const payload = {
      ...modalIntegracaoAi.item,
      idEmpresa: item.value.idEmpresa,
      aplicacoes: modalIntegracaoAi.aplicacoes.filter(a => a.lstAplicacoesAi)
    }

    if (modalIntegracaoAi.item.idClienteIntegracaoAi) {
      await api.put(`/ClienteIntegracoesAi/${modalIntegracaoAi.item.idClienteIntegracaoAi}`, payload)
    } else {
      await api.post('/ClienteIntegracoesAi', payload)
    }

    modalIntegracaoAi.visivel = false
    buscarIntegracoesAi()
    emit('show-notification', { message: 'Integração de IA salva com sucesso!', type: 'success' })
  } catch (error) {
    console.error('Erro ao salvar integração de IA:', error)
    emit('show-notification', { message: 'Erro ao salvar integração de IA.', type: 'danger' })
  } finally {
    modalIntegracaoAi.salvando = false
  }
}

const confirmarExclusaoIntegracaoAi = (id) => {
  exclusaoPendenteIaId.value = id
  confirmacaoIaRef.value?.show()
}

const aoConfirmarExclusaoIa = async (confirmed) => {
  if (!confirmed || exclusaoPendenteIaId.value === null) return

  const id = exclusaoPendenteIaId.value
  exclusaoPendenteIaId.value = null

  try {
    await api.delete(`/ClienteIntegracoesAi/${id}`)
    buscarIntegracoesAi()
    emit('show-notification', { message: 'Integração de IA excluída com sucesso!', type: 'success' })
  } catch (error) {
    console.error('Erro ao excluir integração de IA:', error)
    emit('show-notification', { message: 'Erro ao excluir integração de IA.', type: 'danger' })
  }
}

// --- LÓGICA DE TIPOS DE DOCUMENTO DE ENTRADA (Suprimentos) ---

const documentosEntradaTipos = ref([])

const modalDocumentoEntrada = reactive({
  visivel: false,
  salvando: false,
  item: {
    idTipoDocumentoEntrada: 0,
    idEmpresa: 0,
    nome: '',
    descricao: '',
    isDocumentoInterno: false,
    lstStatus: ST.Ativo,
    siglaDoCodigo: '',
    codigoComAno: false,
    digitosDoCodigo: null,
    caractereSeparacao: ''
  }
})

// Estado reativo para o template dinâmico de partes do Documento de Entrada
const docEntradaPartes = ref([])
const docEntradaSequencialNaoFinal = ref(false)
const docEntradaPreview = ref('')

const buscarDocumentosEntradaTipos = async () => {
  if (!isEditing.value) return
  try {
    const response = await api.get(`/DocumentosEntradaTipos?idEmpresa=${item.value.idEmpresa}`)
    documentosEntradaTipos.value = response.data
  } catch (error) {
    console.error('Erro ao buscar tipos de documento de entrada:', error)
  }
}

const novoDocumentoEntradaTipo = () => {
  modalDocumentoEntrada.item = {
    idTipoDocumentoEntrada: 0,
    idEmpresa: item.value.idEmpresa,
    nome: '',
    descricao: '',
    isDocumentoInterno: false,
    exigePreenchimentoSerie: false,
    lstStatus: ST.Ativo,
    siglaDoCodigo: '',
    codigoComAno: false,
    digitosDoCodigo: null,
    caractereSeparacao: ''
  }
  docEntradaPartes.value = []
  docEntradaPreview.value = ''
  docEntradaSequencialNaoFinal.value = false
  modalDocumentoEntrada.visivel = true
}

const editarDocumentoEntradaTipo = (doc) => {
  modalDocumentoEntrada.item = { ...doc }
  docEntradaPartes.value = doc.configTemplateNumero ? JSON.parse(doc.configTemplateNumero) : []
  atualizarPreviewDocEntrada()
  modalDocumentoEntrada.visivel = true
}

const salvarDocumentoEntradaTipo = async () => {
  modalDocumentoEntrada.salvando = true
  try {
    const payload = {
      ...modalDocumentoEntrada.item,
      idEmpresa: item.value.idEmpresa,
      configTemplateNumero: docEntradaPartes.value.length > 0
        ? JSON.stringify(docEntradaPartes.value)
        : null
    }

    if (modalDocumentoEntrada.item.idTipoDocumentoEntrada) {
      await api.put(`/DocumentosEntradaTipos/${modalDocumentoEntrada.item.idTipoDocumentoEntrada}`, payload)
    } else {
      await api.post('/DocumentosEntradaTipos', payload)
    }

    modalDocumentoEntrada.visivel = false
    buscarDocumentosEntradaTipos()
    emit('show-notification', { message: 'Tipo de Documento salvo com sucesso!', type: 'success' })
  } catch (error) {
    console.error('Erro ao salvar tipo de documento:', error)
    const msg = error.response?.data?.message || error.response?.data?.title || 'Erro ao salvar tipo de documento.'
    emit('show-notification', { message: msg, type: 'danger' })
  } finally {
    modalDocumentoEntrada.salvando = false
  }
}

const excluirDocumentoEntradaTipo = async (id) => {
  if (!confirm('Deseja realmente excluir este tipo de documento de entrada?')) return
  try {
    await api.delete(`/DocumentosEntradaTipos/${id}`)
    buscarDocumentosEntradaTipos()
    emit('show-notification', { message: 'Tipo de Documento excluído com sucesso!', type: 'success' })
  } catch (error) {
    console.error('Erro ao excluir tipo de documento:', error)
    emit('show-notification', { message: 'Erro ao excluir tipo de documento.', type: 'danger' })
  }
}

// --- Funções auxiliares para o template dinâmico de partes do Documento de Entrada ---

const adicionarParteDocEntrada = () => {
  docEntradaPartes.value.push({ tipo: 'separador', valor: '-', digitos: null })
  atualizarPreviewDocEntrada()
}

const removerParteDocEntrada = (index) => {
  docEntradaPartes.value.splice(index, 1)
  atualizarPreviewDocEntrada()
}

const moverParteDocEntrada = (index, direcao) => {
  const novoIndex = index + direcao
  if (novoIndex < 0 || novoIndex >= docEntradaPartes.value.length) return
  const temp = docEntradaPartes.value[index]
  docEntradaPartes.value[index] = docEntradaPartes.value[novoIndex]
  docEntradaPartes.value[novoIndex] = temp
  atualizarPreviewDocEntrada()
}

const aoAlterarTipoParteDocEntrada = (index) => {
  const parte = docEntradaPartes.value[index]
  if (parte.tipo === 'prefixo') {
    parte.valor = parte.valor || ''
    parte.digitos = null
  } else if (parte.tipo === 'separador') {
    parte.valor = parte.valor || '-'
    parte.digitos = null
  } else if (parte.tipo === 'sequencial') {
    parte.valor = null
    parte.digitos = parte.digitos || 5
  } else {
    parte.valor = null
    parte.digitos = null
  }
  atualizarPreviewDocEntrada()
}

const atualizarPreviewDocEntrada = () => {
  const partes = docEntradaPartes.value
  const idxSequencial = partes.findIndex(p => p.tipo === 'sequencial')
  docEntradaSequencialNaoFinal.value = idxSequencial >= 0 && idxSequencial !== partes.length - 1
  docEntradaPreview.value = gerarPreviewTemplate(partes)
}

onMounted(() => {
  // GUARDIÃO: Sincroniza o tenant ativo no header com a empresa sendo editada
  // Isso garante que o AppDbContextTenant conecte no banco tenant correto
  if (isEditing.value && item.value?.idEmpresa) {
    authStore.setEmpresaSelecionada(item.value.idEmpresa)
  }

  carregarListas()
  buscarPermissoesConsolidadas()
  if (isEditing.value) {
    buscarPermissoesDaEmpresa(item.value.idEmpresa)
    buscarTemplates()
    buscarIntegracoesAi()
    buscarDocumentosEntradaTipos()
    buscarTemplatesRelatorio()
    carregarParametrosComissao()
    carregarParametrosComerciais()
    // Deserializar templates de numeração (modal compartilhado)
    templateOrdemCompra.value = parseTemplateNumeracao(item.value.configTemplateNumeroOrdem)
    templatePedidoVenda.value = parseTemplateNumeracao(item.value.configTemplateNumeroPedidoVenda)
    templateProformaInvoice.value = parseTemplateNumeracao(item.value.configTemplateNumeroProformaInvoice)
    templateCommercialInvoice.value = parseTemplateNumeracao(item.value.configTemplateNumeroCommercialInvoice)
    templateContratoVenda.value = parseTemplateNumeracao(item.value.configTemplateNumeroContratoVenda)
    templateContratoCompra.value = parseTemplateNumeracao(item.value.configTemplateNumeroContratoCompra)
    templatePurchaseOrder.value = parseTemplateNumeracao(item.value.configTemplateNumeroPurchaseOrder)
  }
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.accordion-button::after {
  margin-left: auto;
  margin-right: 15px;
}

.accordion-body .list-group-item {
  background-color: transparent;
  border-left: none;
  border-right: none;
  border-radius: 0;
}

.accordion-body .list-group-item:first-child {
  border-top: none;
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.15) !important;
}
</style>
